import React, { useState, useMemo } from 'react';
import { Phone, Mail, MapPin, ExternalLink, Star, Sun, Image, ChevronLeft, ChevronRight, X, Edit, ArrowUp, ArrowDown, Trash2, Sparkles, AlertTriangle, Menu } from 'lucide-react';
import { resolveImageUrl } from '../utils/imageUtils';
import { COLOR_THEMES } from '../constants/colorThemes';

const LivePreview = ({ 
  site, 
  currentPage, 
  sections: pageSections, 
  selectedSection, 
  onSectionSelect, 
  onSectionUpdate, 
  onSectionMove, 
  onSectionDelete, 
  onSectionRegenerate, 
  regeneratingSection 
}) => {
  const [hoveredSection, setHoveredSection] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState(null);

  // All useMemo hooks must be called before early return
  const design = useMemo(() => {
    if (!site) return {};
    
    if (site.isMultiPage && currentPage && !currentPage.isDefault && currentPage.design) {
      // Check if custom theme is enabled for this page
      if (currentPage.design.customThemeEnabled !== false) {
        // For sub-pages with custom theme enabled, merge site design with page-specific design
        return {
          ...site.design,
          ...currentPage.design
        };
      }
    }
    // For main page, single-page sites, or pages with custom theme disabled, use site design
    return site.design || {};
  }, [site, currentPage]);

  const sections = useMemo(() => {
    if (!site) return [];
    
    const baseSections = pageSections || site.sections || [];
    
    // Only apply inheritance for multi-page sites
    if (!site.isMultiPage || !site.pages || site.pages.length <= 1) {
      return baseSections;
    }
    
    // Check if this is the main page
    const isMainPage = currentPage?.isDefault || currentPage?.slug === 'index';
    if (isMainPage) {
      return baseSections;
    }
    
    // For sub-pages, inherit header/footer from main page
    console.log('🔧 [LIVE-PREVIEW] Sub-page detected, inheriting header/footer from main page');
    const mainPage = site.pages.find(p => p.isDefault) || site.pages[0];
    const inheritedHeader = mainPage?.sections?.find(s => s.type === 'header');
    const inheritedFooter = mainPage?.sections?.find(s => s.type === 'footer');
    
    // Merge: inherited header + page content sections + inherited footer
    const contentSections = baseSections.filter(s => s.type !== 'header' && s.type !== 'footer');
    const mergedSections = [
      ...(inheritedHeader ? [inheritedHeader] : []),
      ...contentSections,
      ...(inheritedFooter ? [inheritedFooter] : [])
    ];
    
    console.log('🔧 [LIVE-PREVIEW] Merged sections:', mergedSections.map(s => s.type));
    return mergedSections;
  }, [site, pageSections, currentPage]);

  const colorTheme = useMemo(() => {
    if (!site || !design) {
      return {
        primary: '#3B82F6',
        secondary: '#1F2937',
        accent: '#F59E0B',
        background: '#FFFFFF',
        text: '#1F2937'
      };
    }
    
    // First, try to get colors directly from design.colorTheme
    if (design?.colorTheme?.colors) {
      return design.colorTheme.colors;
    }
    
    // If colorTheme exists and has color properties (legacy format)
    if (design?.colorTheme?.primary) {
      return design.colorTheme;
    }
    
    // If we have a theme name, resolve it from COLOR_THEMES
    if (design?.colorThemeName) {
      const namedTheme = COLOR_THEMES.find(theme => theme.name === design.colorThemeName);
      if (namedTheme) {
        return namedTheme.colors;
      }
    }
    
    // Fallback to default theme
    return {
      primary: '#3B82F6',
      secondary: '#1F2937',
      accent: '#F59E0B',
      background: '#FFFFFF',
      text: '#1F2937'
    };
  }, [site, design]);

  const getUnifiedColorScheme = useMemo(() => (colorName) => {
    // If no colorName specified or 'default', use the site's theme
    if (!colorName || colorName === 'default') {
      return {
        bg: colorTheme.background,
        text: colorTheme.text,
        border: colorTheme.accent,
        accent: colorTheme.primary,
        secondary: colorTheme.secondary
      };
    }
    
    // Check for custom themes first
    if (site?.design && site.design.customThemes && Array.isArray(site.design.customThemes)) {
      const customTheme = site.design.customThemes.find(theme => theme.name === colorName);
      if (customTheme && customTheme.colors) {
        return {
          bg: customTheme.colors.background,
          text: customTheme.colors.text,
          border: customTheme.colors.accent,
          accent: customTheme.colors.primary,
          secondary: customTheme.colors.secondary
        };
      }
    }
    
    // Check for COLOR_THEMES by name first
    const namedTheme = COLOR_THEMES.find(theme => theme.name === colorName);
    if (namedTheme) {
      return {
        bg: namedTheme.colors.background,
        text: namedTheme.colors.text,
        border: namedTheme.colors.accent,
        accent: namedTheme.colors.primary,
        secondary: namedTheme.colors.secondary
      };
    }
    
    // Basic color scheme mapping (used by sections)
    const basicSchemes = {
      'white': { bg: '#ffffff', text: colorTheme.text, border: colorTheme.accent, accent: colorTheme.primary, secondary: colorTheme.secondary },
      'light': { bg: '#f9fafb', text: colorTheme.text, border: colorTheme.accent, accent: colorTheme.primary, secondary: colorTheme.secondary },
      'primary': { bg: colorTheme.primary, text: '#ffffff', border: colorTheme.accent, accent: '#ffffff', secondary: colorTheme.secondary },
      'dark': { bg: '#1f2937', text: '#ffffff', border: '#9CA3AF', accent: '#ffffff', secondary: '#4B5563' }
    };
    
    if (basicSchemes[colorName]) {
      return basicSchemes[colorName];
    }
    
    // Legacy theme mapping for backward compatibility
    const predefinedThemes = {
      slate: { bg: '#F8FAFC', text: '#1E293B', border: '#94A3B8', accent: '#64748B', secondary: '#475569' },
      emerald: { bg: '#ECFDF5', text: '#064E3B', border: '#6EE7B7', accent: '#10B981', secondary: '#047857' },
      blue: { bg: '#EFF6FF', text: '#1E3A8A', border: '#93C5FD', accent: '#3B82F6', secondary: '#1E40AF' },
      violet: { bg: '#F5F3FF', text: '#4C1D95', border: '#C4B5FD', accent: '#8B5CF6', secondary: '#7C3AED' },
      rose: { bg: '#FFF1F2', text: '#881337', border: '#FDA4AF', accent: '#F43F5E', secondary: '#E11D48' },
      amber: { bg: '#FFFBEB', text: '#78350F', border: '#FCD34D', accent: '#F59E0B', secondary: '#D97706' },
      teal: { bg: '#F0FDFA', text: '#134E4A', border: '#5EEAD4', accent: '#14B8A6', secondary: '#0D9488' },
      dark: { bg: '#111827', text: '#F9FAFB', border: '#9CA3AF', accent: '#6B7280', secondary: '#4B5563' },
      neutral: { bg: '#FAFAFA', text: '#262626', border: '#A3A3A3', accent: '#737373', secondary: '#525252' }
    };
    
    const selectedTheme = predefinedThemes[colorName];
    if (selectedTheme) {
      return selectedTheme;
    }
    
    // Fallback to site theme
    return {
      bg: colorTheme.background,
      text: colorTheme.text,
      border: colorTheme.accent,
      accent: colorTheme.primary,
      secondary: colorTheme.secondary
    };
  }, [colorTheme, site?.design?.customThemes]);

  // Early return after all hooks
  if (!site) return null;

  const { businessInfo } = site;

  // Debug logging for theme resolution
  console.log('🎨 [LIVE-PREVIEW] Theme resolution:', {
    'design.colorTheme': design?.colorTheme,
    'design.colorThemeName': design?.colorThemeName,
    'resolved colorTheme': colorTheme,
    'current page': currentPage?.name || 'main',
    'sections': sections?.map(s => ({ type: s.type, colorScheme: s.content?.colorScheme }))
  });

  // Get design properties with fallbacks
  const designProps = {
    fontFamily: design?.fontFamily || 'Inter',
    headingSize: design?.headingSize || 24,
    textSize: design?.textSize || 16,
    headingWeight: design?.headingWeight || 'semibold',
    imageCorners: design?.imageCorners || 'soft',
    buttonCorners: design?.buttonCorners || 'soft',
    bottomBorder: design?.bottomBorder || false,
    maxWidth: design?.maxWidth || 'lg',
    darkMode: design?.darkMode || false
  };

  // Helper functions for styling
  const getHeadingStyle = (level = 1) => {
    const baseSize = designProps.headingSize;
    const sizeMap = {
      1: baseSize + 16, // h1: headingSize + 16px
      2: baseSize + 8,  // h2: headingSize + 8px
      3: baseSize + 4,  // h3: headingSize + 4px
      4: baseSize       // h4: headingSize
    };
    
    const weightMap = {
      light: '300',
      regular: '400',
      medium: '500',
      semibold: '600',
      bold: '700'
    };

    return {
      fontSize: `${sizeMap[level]}px`,
      fontWeight: weightMap[designProps.headingWeight] || '600',
      fontFamily: designProps.fontFamily
    };
  };

  const getTextStyle = () => {
    return {
      fontSize: `${designProps.textSize}px`,
      fontFamily: designProps.fontFamily
    };
  };

  const getImageStyle = () => {
    const radiusMap = {
      square: '0px',
      soft: '4px',
      round: '10px',
      oval: '28px'
    };
    return {
      borderRadius: radiusMap[designProps.imageCorners] || '4px'
    };
  };

  const getButtonStyle = (isPrimary = true) => {
    const radiusMap = {
      square: '0px',
      soft: '4px',
      round: '10px',
      oval: '28px'
    };
    return {
      borderRadius: radiusMap[designProps.buttonCorners] || '4px',
      backgroundColor: isPrimary ? colorTheme.primary : 'transparent',
      color: isPrimary ? colorTheme.background : colorTheme.primary,
      border: isPrimary ? 'none' : `2px solid ${colorTheme.primary}`,
      fontFamily: designProps.fontFamily
    };
  };

  const getSectionBorderStyle = () => {
    return designProps.bottomBorder ? {
      borderBottom: `1px solid ${colorTheme.primary}30`
    } : {};
  };

  const getMaxWidthClass = () => {

    return `max-w-${designProps.maxWidth}` || 'max-w-4xl';
  };

  const getTextShadowStyle = (content) => {
    if (!content?.textShadow?.enabled) return {};
    
    const shadowStyle = content.textShadow.style || 'subtle';
    const shadowColor = content.textShadow.color || 'rgba(0, 0, 0, 0.6)';
    
    const shadowStyles = {
      subtle: '0 1px 2px rgba(0, 0, 0, 0.1)',
      soft: '0 2px 4px rgba(0, 0, 0, 0.2)',
      medium: '0 2px 8px rgba(0, 0, 0, 0.3)',
      strong: '0 4px 12px rgba(0, 0, 0, 0.4)',
      dramatic: '0 6px 16px rgba(0, 0, 0, 0.5)',
      custom: `0 2px 4px ${shadowColor}`
    };
    
    return {
      textShadow: shadowStyles[shadowStyle] || shadowStyles.subtle
    };
  };

  const sectionComponents = {
    header: HeaderSection,
    hero: HeroSection,
    faq: FAQSection,
    services: ServicesSection,
    gallery: GallerySection,
    testimonials: TestimonialsSection,
    team: TeamSection,
    features: FeaturesSection,
    pricing: PricingSection,
    cta: CTASection,
    article: ArticleSection,
    contact: ContactSection,
    newsletter: NewsletterSection,
    footer: FooterSection,
    iframe: IframeSection,
    banner: BannerSection,
    'custom-code': CustomCodeSection
  };

  const getSectionStyle = (section) => ({
    fontFamily: designProps.fontFamily,
    outline: selectedSection?.id === section.id ? '4px solid rgba(35, 30, 68, 0.6)' : 'none',
    outlineOffset: selectedSection?.id === section.id ? '-4px' : '0',
    cursor: 'pointer',
    transition: 'outline 0.2s ease',
    ...getSectionBorderStyle()
  });

  // Confirmation Modal Component
  const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, confirmText, confirmColor = "red" }) => {
    // Handle escape key
    React.useEffect(() => {
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
      }
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all animate-in zoom-in-95 duration-200">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmColor === 'red' ? 'bg-red-100' : 'bg-purple-100'
              }`}>
                {confirmColor === 'red' ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : (
                  <Sparkles className="w-5 h-5 text-purple-600" />
                )}
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">
                {title}
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              {message}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  confirmColor === 'red' 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
                }`}
                autoFocus
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Section overlay component with action buttons
  const SectionOverlay = ({ section, isVisible, sortedSections, regeneratingSection }) => {
    if (!isVisible || regeneratingSection === section.id) return null;

    const currentIndex = sortedSections.findIndex(s => s.id === section.id);
    const canMoveUp = currentIndex > 0 && sortedSections[currentIndex - 1].type !== 'header';
    const canMoveDown = currentIndex < sortedSections.length - 1 && sortedSections[currentIndex + 1].type !== 'footer';
    const canDelete = section.type !== 'header' && section.type !== 'footer';
    const canRegenerate = ['hero', 'faq', 'services'].includes(section.type) && !regeneratingSection;

    const handleAction = (action, e) => {
      e.stopPropagation();
      e.preventDefault();
      
      switch (action) {
        case 'edit':
          onSectionSelect?.(section);
          break;
        case 'up':
          if (canMoveUp) {
            onSectionMove?.(section.id, 'up');
          }
          break;
        case 'down':
          if (canMoveDown) {
            onSectionMove?.(section.id, 'down');
          }
          break;
        case 'delete':
          if (canDelete) {
            setConfirmationModal({
              type: 'delete',
              section,
              title: `Delete ${section.type} section`,
              message: `Are you sure you want to delete the ${section.type} section? This action cannot be undone.`,
              confirmText: 'Delete Section',
              confirmColor: 'red',
              onConfirm: () => onSectionDelete?.(section.id)
            });
          }
          break;
        case 'regenerate':
          if (canRegenerate) {
            setConfirmationModal({
              type: 'regenerate',
              section,
              title: `Regenerate ${section.type} content`,
              message: `Are you sure you want to regenerate the ${section.type} section content with AI? This will replace the current content with fresh, new content.`,
              confirmText: 'Regenerate with AI',
              confirmColor: 'purple',
              onConfirm: () => onSectionRegenerate?.(section.id)
            });
          }
          break;
        default:
          break;
      }
    };

    return (
      <div className="absolute inset-0 bg-black bg-opacity-20 z-10 pointer-events-none">
        <div className="absolute top-4 right-4 flex space-x-2 pointer-events-auto">
          {/* Edit Button */}
          <button
            onClick={(e) => handleAction('edit', e)}
            className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
            title="Edit Section"
          >
            <Edit className="w-4 h-4" />
          </button>

          {/* Move Up Button */}
          {canMoveUp && (
            <button
              onClick={(e) => handleAction('up', e)}
              className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
              title="Move Up"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          )}

          {/* Move Down Button */}
          {canMoveDown && (
            <button
              onClick={(e) => handleAction('down', e)}
              className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
              title="Move Down"
            >
              <ArrowDown className="w-4 h-4" />
            </button>
          )}

          {/* Regenerate Button */}
          {canRegenerate && (
            <button
              onClick={(e) => handleAction('regenerate', e)}
              className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center hover:bg-purple-700 transition-colors shadow-lg"
              title="Regenerate with AI"
            >
              <Sparkles className="w-4 h-4" />
            </button>
          )}

          {/* Delete Button */}
          {canDelete && (
            <button
              onClick={(e) => handleAction('delete', e)}
              className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-lg"
              title="Delete Section"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Section Label */}
        <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white px-3 py-1 rounded-full text-sm font-medium capitalize pointer-events-auto">
          {section.type} Section
        </div>
      </div>
    );
  };

  const sortedSections = sections
    .filter(section => section.visible)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <div className={`bg-white max-w-${designProps.maxWidth} rounded-lg overflow-hidden min-h-full relative`}>
        {sortedSections.map((section) => {
          
          const SectionComponent = sectionComponents[section.type];
          if (!SectionComponent) return null;

          return (
            <div
              key={section.id}
              style={getSectionStyle(section)}
              onClick={(e) => {
                e.stopPropagation();
                if (regeneratingSection !== section.id) {
                  onSectionSelect?.(section);
                }
              }}
              onMouseEnter={() => {
                if (regeneratingSection !== section.id) {
                  setHoveredSection(section.id);
                }
              }}
              onMouseLeave={() => setHoveredSection(null)}
              className={`relative transition-all duration-300 ${
                regeneratingSection === section.id 
                  ? 'opacity-60 pointer-events-none' 
                  : ''
              }`}
            >
              <SectionComponent
                section={section}
                colorTheme={colorTheme}
                businessInfo={businessInfo}
                designProps={designProps}
                getHeadingStyle={getHeadingStyle}
                getTextStyle={getTextStyle}
                getImageStyle={getImageStyle}
                getButtonStyle={getButtonStyle}
                getMaxWidthClass={getMaxWidthClass}
                getTextShadowStyle={getTextShadowStyle}
                selectedSection={selectedSection}
                getUnifiedColorScheme={getUnifiedColorScheme}
              />
              
              {/* Section Overlay */}
              <SectionOverlay
                section={section}
                isVisible={hoveredSection === section.id}
                sortedSections={sortedSections}
                regeneratingSection={regeneratingSection}
              />
              
              {/* Regenerating Overlay */}
              {regeneratingSection === section.id && (
                <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-40 rounded-lg">
                  <div className="text-center">
                    <div className="w-12 h-12 mx-auto mb-3 relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
                      <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center">
                        <span className="text-xl">✨</span>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-1">AI is redesigning...</p>
                    <p className="text-xs text-gray-500">Please wait while magic happens</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Cookie Banner for Preview - Inside container */}
        {site.settings?.cookieBanner && <CookieBannerPreview colorTheme={colorTheme} designProps={designProps} />}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!confirmationModal}
        onClose={() => setConfirmationModal(null)}
        onConfirm={confirmationModal?.onConfirm}
        title={confirmationModal?.title}
        message={confirmationModal?.message}
        confirmText={confirmationModal?.confirmText}
        confirmColor={confirmationModal?.confirmColor}
      />
    </>
  );
};

// Header Section Component
const HeaderSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getButtonStyle, getMaxWidthClass, selectedSection }) => {
  const content = section.content || {};
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on window resize or escape key
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) { // lg breakpoint
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleEscape);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);
  
  // Get section-specific colors if available, otherwise use site theme
  const getUnifiedColorScheme = (schemeName) => {
    if (!schemeName || schemeName === 'default') {
      return colorTheme;
    }
    
    // Import and use the same color themes as the editor
    const { COLOR_THEMES } = require('../constants/colorThemes');
    const selectedTheme = COLOR_THEMES.find(theme => theme.name === schemeName);
    
    if (selectedTheme) {
      return {
        primary: selectedTheme.colors?.primary || selectedTheme.primary,
        secondary: selectedTheme.colors?.secondary || selectedTheme.secondary,
        accent: selectedTheme.colors?.accent || selectedTheme.accent,
        background: selectedTheme.colors?.background || selectedTheme.background,
        text: selectedTheme.colors?.text || selectedTheme.text
      };
    }
    
    return colorTheme;
  };

  const sectionColorScheme = getUnifiedColorScheme(content.colorScheme || 'default');
  
  // Handle inverted colors with section-specific theme
  const headerColors = content.invertedColors ? {
    background: sectionColorScheme.primary,
    text: sectionColorScheme.background,
    border: sectionColorScheme.background + '30'
  } : {
    background: sectionColorScheme.background,
    text: sectionColorScheme.text,
    border: sectionColorScheme.primary + '30'
  };
  
  // Get style-based classes
  const getStyleClasses = () => {
    const style = content.style || 'clean';
    switch (style) {
      case 'minimal':
        return 'py-2 px-6 border-0';
      case 'bold':
        return 'py-6 px-6 border-b-2';
      case 'elegant':
        return 'py-5 px-6 border-b shadow-sm';
      case 'clean':
      default:
        return 'py-4 px-6 border-b';
    }
  };
  
  // Get layout-based classes
  const getLayoutClasses = () => {
    const layout = content.layout || 'horizontal';
    switch (layout) {
      case 'centered':
        return 'flex-col items-center space-y-4';
      case 'stacked':
        return 'flex-col space-y-3';
      case 'horizontal':
      default:
        return 'flex-row items-center justify-between';
    }
  };
  
  return (
    <header 
      className={`w-full ${getStyleClasses()}`}
      style={{ 
        backgroundColor: headerColors.background,
        borderColor: headerColors.border,
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto flex ${getLayoutClasses()} px-3 sm:px-4 lg:px-6`}>
        {/* Logo Section - Responsive */}
        <div className="flex items-center min-w-0 flex-shrink-0" style={{ 
          color: headerColors.text,
          fontFamily: designProps.fontFamily
        }}>
          {(content.logoImage || content.logoImageUrl) ? (
            <div className="flex items-center space-x-1.5 sm:space-x-2 min-w-0">
              <img 
                src={resolveImageUrl(content.logoImageUrl || content.logoImage)} 
                alt={content.companyName || content.businessName || businessInfo?.name || 'Logo'}
                className={`object-contain flex-shrink-0 ${
                  content.logoHeight === 'small' ? 'h-5 sm:h-6 lg:h-8' :
                  content.logoHeight === 'large' ? 'h-7 sm:h-8 lg:h-12' :
                  content.logoHeight === 'xl' ? 'h-9 sm:h-10 lg:h-14' : 'h-6 sm:h-8 lg:h-10'
                }`}
              />
              {(content.logoPosition !== 'center') && (
                <span className="text-sm sm:text-base lg:text-lg font-bold truncate min-w-0">
                  {content.companyName || businessInfo?.name || content.businessName || 'Your Business'}
                </span>
              )}
              {/* Hidden fallback text for accessibility */}
              {(content.logoPosition === 'center') && (
                <span className="sr-only">
                  {content.companyName || businessInfo?.name || content.businessName || 'Your Business'}
                </span>
              )}
            </div>
          ) : (
            <div className="text-sm sm:text-base lg:text-lg font-bold truncate min-w-0">
              {(() => {
                // FIXED: Prioritize fresh businessInfo.name over potentially stale content.businessName
                const displayName = content.companyName || businessInfo?.name || content.businessName || 'Your Business';
                console.log('🏢 Header rendering name (FIXED):', {
                  contentCompanyName: content.companyName,
                  contentBusinessName: content.businessName,
                  businessInfoName: businessInfo?.name,
                  finalDisplayName: displayName,
                  priorityOrder: 'companyName → businessInfo.name → businessName → fallback'
                });
                return displayName;
              })()}
            </div>
          )}
        </div>
        
        {/* Navigation - Desktop Only */}
        <nav className={`hidden lg:flex items-center ${content.layout === 'centered' || content.layout === 'stacked' ? 'justify-center' : ''} space-x-4 xl:space-x-6 flex-1 justify-center`}>
          {(content.navigation || ['About', 'Services', 'Contact']).map((item, index) => {
            // Handle both string format (legacy) and object format (new)
            const text = typeof item === 'string' ? item : (item.text || item.label || 'Link');
            const url = typeof item === 'string' ? `#${item.toLowerCase()}` : (item.url || item.link || `#${text.toLowerCase()}`);
            
            return (
              <a
                key={index}
                href={url}
                className="hover:opacity-80 transition text-xs sm:text-sm whitespace-nowrap px-2 py-1 rounded-md"
                style={{ 
                  color: headerColors.text,
                  fontFamily: designProps.fontFamily
                }}
              >
                {text}
              </a>
            );
          })}
        </nav>

        {/* Actions Section - Responsive */}
        <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
          {/* Dark Mode Toggle */}
          {designProps.darkMode && (
            <button
              className="p-1.5 sm:p-2 rounded-lg transition hover:bg-gray-100"
              style={{ color: headerColors.text }}
              title="Toggle dark mode"
            >
              <Sun className="w-4 h-4" />
            </button>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Don't toggle mobile menu if a section is selected (editing mode)
              if (selectedSection) return;
              setMobileMenuOpen(!mobileMenuOpen);
            }}
            className="lg:hidden p-1.5 sm:p-2 rounded-lg transition-all duration-200 hover:bg-gray-100 active:scale-95"
            style={{ 
              color: headerColors.text,
              backgroundColor: mobileMenuOpen ? (headerColors.text + '10') : 'transparent'
            }}
            title="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

          {content.showButton !== false && (
            <a
              href={content.buttonUrl || '#contact'}
              className="hidden sm:inline-block px-4 py-2 font-medium transition hover:opacity-90 text-sm whitespace-nowrap"
              style={content.invertedColors ? {
                ...getButtonStyle(false),
                backgroundColor: 'transparent',
                color: headerColors.text,
                border: `2px solid ${headerColors.text}`
              } : getButtonStyle(true)}
            >
              {content.buttonText || 'Get Started'}
            </a>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-20 z-40 lg:hidden"
            onClick={() => {
              // Don't close mobile menu if a section is selected (editing mode)
              if (selectedSection) return;
              setMobileMenuOpen(false);
            }}
          />
          
          {/* Menu Content */}
          <div 
            className="lg:hidden border-t relative z-50 animate-in slide-in-from-top-2 duration-200"
            style={{ 
              backgroundColor: headerColors.background,
              borderColor: headerColors.border
            }}
          >
            <div className={`${getMaxWidthClass()} mx-auto py-3 px-3 sm:px-4 lg:px-6`}>
            <nav className="flex flex-col space-y-1">
              {(content.navigation || ['About', 'Services', 'Contact']).map((item, index) => {
                const text = typeof item === 'string' ? item : (item.text || item.label || 'Link');
                const url = typeof item === 'string' ? `#${item.toLowerCase()}` : (item.url || item.link || `#${text.toLowerCase()}`);
                
                return (
                  <a
                    key={index}
                    href={url}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Don't close mobile menu if a section is selected (editing mode)
                      if (selectedSection) return;
                      setMobileMenuOpen(false);
                    }}
                    className="py-2.5 px-3 rounded-md transition text-sm font-medium block"
                    style={{ 
                      color: headerColors.text,
                      fontFamily: designProps.fontFamily,
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = content.invertedColors ? 
                        (headerColors.text + '20') : 
                        (headerColors.text + '10');
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    {text}
                  </a>
                );
              })}
              
              {/* Mobile CTA Button */}
              {content.showButton !== false && (
                <div className="pt-4 mt-2 border-t" style={{ borderColor: headerColors.border }}>
                  <a
                    href={content.buttonUrl || '#contact'}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Don't close mobile menu if a section is selected (editing mode)
                      if (selectedSection) return;
                      setMobileMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 font-medium transition hover:opacity-90 text-base inline-block text-center rounded-md"
                    style={content.invertedColors ? {
                      ...getButtonStyle(false),
                      backgroundColor: 'transparent',
                      color: headerColors.text,
                      border: `2px solid ${headerColors.text}`
                    } : getButtonStyle(true)}
                  >
                    {content.buttonText || 'Get Started'}
                  </a>
                </div>
              )}
            </nav>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

// Hero Section Component
const HeroSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getTextStyle, getButtonStyle, getMaxWidthClass, getTextShadowStyle }) => {
  const content = section.content || {};
  
  // Handle custom color themes
  const heroColors = content.colorScheme ? (() => {
    const themes = {
      light: { bg: '#f8f9fa', text: '#212529', accent: '#dc3545' },
      gray: { bg: '#e9ecef', text: '#495057', accent: '#dc3545' },
      blue: { bg: '#dee2e6', text: '#343a40', accent: '#dc3545' },
      dark1: { bg: '#343a40', text: '#ffffff', accent: '#ffffff' },
      dark2: { bg: '#212529', text: '#ffffff', accent: '#ffffff' },
      wine: { bg: '#4a0e0e', text: '#ffffff', accent: '#ffffff' }
    };
    return themes[content.colorTheme] || { bg: colorTheme.background, text: colorTheme.text, accent: colorTheme.primary };
  })() : { bg: colorTheme.background, text: colorTheme.text, accent: colorTheme.primary };

  // Handle text alignment
  const getAlignment = () => {
    const alignment = content.alignment || 'center';
    return alignment === 'left' ? 'text-left' : alignment === 'right' ? 'text-right' : 'text-center';
  };

  // Handle background image
  const getBackgroundStyle = () => {
    let style = {
      backgroundColor: heroColors.bg,
      fontFamily: designProps.fontFamily
    };
    
    if (content.backgroundImage || content.backgroundImageUrl) {
      const imageUrl = content.backgroundImageUrl || content.backgroundImage || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200';
      style.backgroundImage = `url("${resolveImageUrl(imageUrl)}")`;
      style.backgroundSize = content.backgroundStyle === 'fit' ? 'contain' : 'cover';
      style.backgroundPosition = 'center';
      style.backgroundRepeat = 'no-repeat';
      
      if (content.overlayOpacity) {
        style.position = 'relative';
      }
    }
    
    return style;
  };
      // Hero height mapping
      const heroHeightClasses = {
        small: 'min-h-[30vh]',
        medium: 'min-h-[50vh]',
        large: 'min-h-[70vh]',
        full: 'min-h-screen'
      };
      const heroHeight = heroHeightClasses[content.heroHeight] || heroHeightClasses.medium;
  return (
    <section 
      className={`py-16 px-6 w-full ${heroHeight} relative`}
      style={getBackgroundStyle()}
    >
      
      {/* Background overlay */}
      {content.backgroundImage && content.overlayOpacity && (
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundColor: heroColors.bg,
            opacity: content.overlayOpacity / 100 
          }}
        />
        
      )}
      
      <div className={`${getMaxWidthClass()} mx-auto ${getAlignment()} relative z-10`}>
        {/* Content Layout with Foreground Image */}
        <div className={`${content.foregroundImage ? 'grid grid-cols-1 md:grid-cols-2 gap-8 items-center' : ''}`}>
          
          {/* Text Content */}
          <div className={content.foregroundImage && content.foregroundPlacement === 'right' ? 'md:order-1' : 'md:order-2'}>
            <h1 
              className="mb-6 leading-tight"
              style={{ 
                color: heroColors.text,
                ...getHeadingStyle(1),
                ...getTextShadowStyle(content)
              }}
            >
              {content.headline || `Welcome to ${businessInfo?.name || 'Your Business'}`}
            </h1>
            
            <p 
              className="mb-8 max-w-2xl leading-relaxed"
              style={{ 
                color: heroColors.text, 
                opacity: 0.8,
                ...getTextStyle(),
                margin: content.alignment === 'center' ? '0 auto 2rem auto' : '0 0 2rem 0',
                ...getTextShadowStyle(content)
              }}
            >
              {content.subheading || businessInfo?.description || 'Your trusted partner for professional services'}
            </p>

            {/* Buttons */}
            {content.showButton !== false && (
              <div className={`flex gap-4 ${content.alignment === 'center' ? 'justify-center' : content.alignment === 'right' ? 'justify-end' : 'justify-start'} items-center flex-wrap`}>
                <a
                  href={content.ctaUrl || '#contact'}
                  className="px-6 py-3 font-semibold transition hover:opacity-90 inline-block"
                  style={{
                    ...getButtonStyle(true),
                    backgroundColor: heroColors.accent,
                    color: heroColors.bg
                  }}
                >
                  {content.ctaText || 'Get Started'}
                </a>
              </div>
            )}
          </div>

          {/* Foreground Image */}
          {content.foregroundImage && (
            <div className={`flex ${content.alignment === 'center' ? 'justify-center' : content.alignment === 'right' ? 'justify-end' : 'justify-start'} ${content.foregroundPlacement === 'right' ? 'md:order-2' : 'md:order-1'}`}>
              <div 
                className="relative"
                style={{
                  maxWidth: (() => {
                    const size = content.foregroundSize || 'md';
                    switch (size) {
                      case 'sm': return '280px';
                      case 'md': return '400px';
                      case 'lg': return '520px';
                      default: return '400px';
                    }
                  })(),
                  width: '100%'
                }}
              >
                <div
                  key={`${content.foregroundFrame}-${content.foregroundShape}`}
                  className="w-full bg-gray-200 flex items-center justify-center overflow-hidden"
                  style={{
                    aspectRatio: (() => {
                      const frame = content.foregroundFrame || 'wide';
                      switch (frame) {
                        case 'wide': return '16 / 9';
                        case 'landscape': return '4 / 3';
                        case 'portrait': return '3 / 4';
                        case 'square': return '1 / 1';
                        case 'circle': return '1 / 1';
                        default: return '16 / 9';
                      }
                    })(),
                    backgroundImage: content.foregroundImageUrl ? `url("${resolveImageUrl(content.foregroundImageUrl)}")` : 'url("https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400")', // Placeholder cafe image
                    backgroundSize: content.foregroundStyle === 'fit' ? 'contain' : 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: (() => {
                      const frame = content.foregroundFrame || 'wide';
                      const shape = content.foregroundShape || 'none';
                      
                      // Only apply borderRadius if no clipPath will be used
                      if (frame === 'circle' && shape === 'none') {
                        return '50%';
                      } else if (shape === 'none') {
                        return '8px';
                      }
                      return '0';
                    })(),
                    WebkitMask: (() => {
                      const frame = content.foregroundFrame || 'wide';
                      const shape = content.foregroundShape || 'none';
                      
                      // For circle frame, always use circle mask unless shape is none
                      if (frame === 'circle' && shape !== 'none') {
                        return 'radial-gradient(circle at center, black 50%, transparent 50%)';
                      }
                      
                      // For other frames, only apply shape if not none
                      if (shape === 'none') return 'none';
                      
                      // Only use compatible shape-frame combinations
                      const frameShapeCompatibility = {
                        'wide': ['angled', 'pill'],
                        'landscape': ['notched', 'pill'],
                        'portrait': ['arch', 'pill'],
                        'square': ['angled', 'bumpy', 'flared', 'flower', 'screen']
                      };
                      
                      const compatibleShapes = frameShapeCompatibility[frame] || [];
                      if (!compatibleShapes.includes(shape)) {
                        return 'none';
                      }
                      
                      // Map shape names to SVG mask files
                      const getMaskUrl = (shapeName, frameName) => {
                        const shapeFrameMasks = {
                          'angled': {
                            'square': '/masks/Angled-Square.svg',
                            'wide': '/masks/Angled-Wide.svg'
                          },
                          'bumpy': {
                            'square': '/masks/Bumpy-Square.svg'
                          },
                          'flared': {
                            'square': '/masks/Flared-Square.svg'
                          },
                          'flower': {
                            'square': '/masks/Flower-Square.svg'
                          },
                          'screen': {
                            'square': '/masks/Screen-Square.svg'
                          },
                          'notched': {
                            'landscape': '/masks/Notched-Landscape.svg'
                          },
                          'arch': {
                            'portrait': '/masks/Arch-Portrait.svg'
                          },
                          'pill': {
                            'landscape': '/masks/Pill-Landscape.svg',
                            'portrait': '/masks/Pill-Portrait.svg',
                            'wide': '/masks/Pill-Wide.svg'
                          }
                        };
                        
                        const shapeMap = shapeFrameMasks[shapeName];
                        if (shapeMap && shapeMap[frameName]) {
                          return `url("${process.env.PUBLIC_URL || ''}${shapeMap[frameName]}")`;
                        }
                        
                        return 'none';
                      };
                      
                      return getMaskUrl(shape, frame);
                    })(),
                    WebkitMaskSize: 'cover',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    WebkitMaskComposite: 'intersect',
                    mask: (() => {
                      const frame = content.foregroundFrame || 'wide';
                      const shape = content.foregroundShape || 'none';
                      
                      // For circle frame, always use circle mask unless shape is none
                      if (frame === 'circle' && shape !== 'none') {
                        return 'radial-gradient(circle at center, black 50%, transparent 50%)';
                      }
                      
                      // For other frames, only apply shape if not none
                      if (shape === 'none') return 'none';
                      
                      // Only use compatible shape-frame combinations
                      const frameShapeCompatibility = {
                        'wide': ['angled', 'pill'],
                        'landscape': ['notched', 'pill'],
                        'portrait': ['arch', 'pill'],
                        'square': ['angled', 'bumpy', 'flared', 'flower', 'screen']
                      };
                      
                      const compatibleShapes = frameShapeCompatibility[frame] || [];
                      if (!compatibleShapes.includes(shape)) {
                        return 'none';
                      }
                      
                      // Map shape names to SVG mask files
                      const getMaskUrl = (shapeName, frameName) => {
                        const shapeFrameMasks = {
                          'angled': {
                            'square': '/masks/Angled-Square.svg',
                            'wide': '/masks/Angled-Wide.svg'
                          },
                          'bumpy': {
                            'square': '/masks/Bumpy-Square.svg'
                          },
                          'flared': {
                            'square': '/masks/Flared-Square.svg'
                          },
                          'flower': {
                            'square': '/masks/Flower-Square.svg'
                          },
                          'screen': {
                            'square': '/masks/Screen-Square.svg'
                          },
                          'notched': {
                            'landscape': '/masks/Notched-Landscape.svg'
                          },
                          'arch': {
                            'portrait': '/masks/Arch-Portrait.svg'
                          },
                          'pill': {
                            'landscape': '/masks/Pill-Landscape.svg',
                            'portrait': '/masks/Pill-Portrait.svg',
                            'wide': '/masks/Pill-Wide.svg'
                          }
                        };
                        
                        const shapeMap = shapeFrameMasks[shapeName];
                        if (shapeMap && shapeMap[frameName]) {
                          return `url("${process.env.PUBLIC_URL || ''}${shapeMap[frameName]}")`;
                        }
                        
                        return 'none';
                      };
                      
                      return getMaskUrl(shape, frame);
                    })(),

                    // Apply size scaling through transform
                    transform: (() => {
                      const size = content.foregroundSize || 'md';
                      switch (size) {
                        case 'sm': return 'scale(0.8)';
                        case 'md': return 'scale(1)';
                        case 'lg': return 'scale(1.2)';
                        default: return 'scale(1)';
                      }
                    })(),
                    WebkitMaskSize: 'cover',
                    WebkitMaskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskSize: 'cover',
                    maskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    // Force mask properties to be applied
                    WebkitMaskOrigin: 'border-box',
                    maskOrigin: 'border-box'
                  }}
                >
                  {!content.foregroundImageUrl && (
                    <div className="text-center text-gray-500">
                      <Image className="w-12 h-12 mx-auto mb-2" />
                      <div>Foreground Image</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// About Section Component
const FAQSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getTextStyle, getMaxWidthClass, selectedSection, getUnifiedColorScheme }) => {
  const content = section.content || {};
  const faqs = content.faqs || [
    { question: 'What services do you offer?', answer: 'We offer comprehensive services tailored to meet your specific needs and exceed your expectations.' },
    { question: 'How long have you been in business?', answer: 'We have been serving our community with dedication and expertise for several years.' },
    { question: 'What makes you different from competitors?', answer: 'Our commitment to quality, personalized service, and customer satisfaction sets us apart from the competition.' }
  ];
  
  const [expandedFAQ, setExpandedFAQ] = React.useState(content.expandFirst !== false ? 0 : null);
  
  // Get unified color scheme - use content.colorTheme for consistency with HTML generator
  const faqColors = getUnifiedColorScheme ? getUnifiedColorScheme(content.colorScheme || 'default') : {
    bg: colorTheme.background === '#FFFFFF' ? '#F9FAFB' : colorTheme.background,
    text: colorTheme.text,
    accent: colorTheme.accent,
    primary: colorTheme.primary
  };
  
  return (
    <section 
      className="py-12 px-6 w-full"
      style={{ 
        backgroundColor: faqColors.bg,
        color: faqColors.text,
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto`}>
        {content.showHeading !== false && (
          <div className="text-center mb-12">
            <h2 
              className="mb-4"
              style={{ 
                color: faqColors.text,
                ...getHeadingStyle(2)
              }}
            >
              {content.title || 'Frequently Asked Questions'}
            </h2>
            {content.description && (
              <p 
                className="text-lg max-w-2xl mx-auto"
                style={{ 
                  color: faqColors.text,
                  opacity: 0.9,
                  fontFamily: designProps.fontFamily,
                  textAlign: 'center'
                }}
              >
                {content.description}
              </p>
            )}
          </div>
        )}
        
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => {
              // Card background calculation for better contrast
              const getCardBg = () => {
                const isLightBg = faqColors.bg === '#FFFFFF' || faqColors.bg.startsWith('#F') || faqColors.bg.startsWith('#f');
                if (isLightBg) {
                  return '#FFFFFF';
                } else {
                  // Make card slightly lighter than background for dark themes
                  if (faqColors.bg.length === 7 && faqColors.bg.startsWith('#')) {
                    const r = parseInt(faqColors.bg.slice(1, 3), 16);
                    const g = parseInt(faqColors.bg.slice(3, 5), 16);
                    const b = parseInt(faqColors.bg.slice(5, 7), 16);
                    const lighter = (color) => Math.min(255, Math.floor(color * 1.15));
                    return `rgb(${lighter(r)}, ${lighter(g)}, ${lighter(b)})`;
                  }
                  return faqColors.bg;
                }
              };

              return (
              <div 
                key={index} 
                className="border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-md"
                style={{ 
                  backgroundColor: getCardBg(),
                  borderColor: faqColors.accent
                }}
              >
                <button
                  onClick={() => {
                    // Don't expand/collapse if a section is selected (editing mode)
                    if (selectedSection) return;
                    setExpandedFAQ(expandedFAQ === index ? null : index);
                  }}
                  className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  style={{ 
                    backgroundColor: expandedFAQ === index ? faqColors.primary : faqColors.accent,
                    color: expandedFAQ === index && faqColors.primary === faqColors.text ? faqColors.bg : faqColors.text
                  }}
                >
                  <h3 
                    className="font-medium text-lg pr-4"
                    style={{ 
                      color: expandedFAQ === index && faqColors.primary === faqColors.text ? faqColors.bg : faqColors.text,
                      fontFamily: designProps.fontFamily
                    }}
                  >
                    {faq.question || 'Enter your question...'}
                  </h3>
                  <div 
                    className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full transition-transform duration-200"
                    style={{ 
                      backgroundColor: `${faqColors.primary}20`,
                      transform: expandedFAQ === index ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                  >
                    <svg 
                      className="w-4 h-4" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                      style={{ color: faqColors.text }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                
                {expandedFAQ === index && (
                  <div 
                    className="px-6 pb-4 border-t"
                    style={{ borderColor: faqColors.accent }}
                  >
                    <p 
                      className="leading-relaxed pt-4"
                      style={{ 
                        color: faqColors.text,
                        opacity: faq.answer ? 0.8 : 0.5,
                        ...getTextStyle()
                      }}
                    >
                      {faq.answer || 'Provide a detailed answer...'}
                    </p>
                  </div>
                )}
              </div>
            )})}
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section Component
const ServicesSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getTextStyle, getImageStyle, getMaxWidthClass, getUnifiedColorScheme }) => {
  const content = section.content || {};
  const services = content.services || [
    { title: 'Service 1', description: 'Professional service tailored to your needs', imageUrl: '' },
    { title: 'Service 2', description: 'Quality solutions for your business', imageUrl: '' },
    { title: 'Service 3', description: 'Expert consultation and support', imageUrl: '' }
  ];
  
  // Get unified color scheme - use content.colorScheme for consistency with section editors
  const colorScheme = getUnifiedColorScheme(content.colorScheme || 'default');
  
  // Get frame styles
  const getFrameStyle = () => {
    const frame = content.imageFrame || 'landscape';
    switch (frame) {
      case 'square':
        return { aspectRatio: '1/1' };
      case 'portrait':
        return { aspectRatio: '3/4' };
      case 'circle':
        return { aspectRatio: '1/1', borderRadius: '50%' };
      default: // landscape
        return { aspectRatio: '4/3' };
    }
  };
  
  // Get border styles
  const getBorderStyle = () => {
    const border = content.imageBorder || 'none';
    switch (border) {
      case 'all':
        return { border: `2px solid ${colorScheme.border}` };
      case 'bottom':
        return { borderBottom: `2px solid ${colorScheme.border}` };
      default:
        return {};
    }
  };
  
  // Get text alignment
  const getTextAlignment = () => {
    const align = content.textAlign || 'left';
    return {
      textAlign: align,
      alignItems: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'
    };
  };
  
  // Handle background image
  const getBackgroundStyle = () => {
    let style = {
      backgroundColor: colorScheme.bg,
      fontFamily: designProps.fontFamily
    };
    
    if (content.backgroundImage || content.backgroundImageUrl) {
      const imageUrl = content.backgroundImageUrl || content.backgroundImage;
      style.backgroundImage = `url("${resolveImageUrl(imageUrl)}")`;
      style.backgroundSize = content.backgroundStyle === 'fit' ? 'contain' : 'cover';
      style.backgroundPosition = 'center';
      style.backgroundRepeat = 'no-repeat';
      
      if (content.overlayOpacity) {
        style.position = 'relative';
      }
    }
    
    return style;
  };

  return (
    <section 
      className="py-20 px-6 relative"
      style={getBackgroundStyle()}
    >
      {/* Background overlay */}
      {(content.backgroundImage || content.backgroundImageUrl) && content.overlayOpacity && (
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundColor: colorScheme.bg,
            opacity: content.overlayOpacity / 100 
          }}
        />
      )}
      <div className={`${getMaxWidthClass()} mx-auto relative z-10`}>
        {/* Section Title */}
        {content.showHeading !== false && (
          <div className="mb-12" style={getTextAlignment()}>
            <h2 
              className="mb-4"
              style={{ 
                color: colorScheme.text,
                ...getHeadingStyle(2),
                textAlign: content.textAlign || 'center'
              }}
            >
              {content.title || 'Our Services'}
            </h2>
            {content.description && (
              <p 
                className="text-lg max-w-2xl mx-auto"
                style={{ 
                  color: colorScheme.text,
                  opacity: 0.9,
                  fontFamily: designProps.fontFamily,
                  textAlign: content.textAlign || 'center'
                }}
              >
                {content.description}
              </p>
            )}
          </div>
        )}
        
        {/* Services Grid */}
        <div className={`grid gap-6 ${(() => {
          const layout = content.layout || 'auto';
          const serviceCount = services.length;
          
          // Manual layout selection
          if (layout === 'single') {
            return 'grid-cols-1 max-w-md mx-auto';
          } else if (layout === 'two-column') {
            return 'grid-cols-1 md:grid-cols-2';
          } else if (layout === 'three-column') {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
          } else if (layout === 'four-column') {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
          }
          
          // Auto/Smart grid layout based on number of services
          if (serviceCount === 1) {
            return 'grid-cols-1 max-w-md mx-auto'; // Single service, centered
          } else if (serviceCount === 2) {
            return 'grid-cols-1 md:grid-cols-2'; // Two services, side by side on medium+
          } else if (serviceCount === 3) {
            return 'grid-cols-1 md:grid-cols-3'; // Three services, all in one row on medium+
          } else if (serviceCount === 4) {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'; // Four services, 2x2 on medium, 4 in row on large
          } else if (serviceCount === 5) {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'; // Five services, responsive
          } else if (serviceCount === 6) {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'; // Six services, 2 rows of 3
          } else {
            return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'; // Many services, max 4 per row
          }
        })()}`}>
          {services.map((service, index) => (
            <div
              key={index}
              className="overflow-hidden"
              style={{ 
                backgroundColor: (() => {
                  const baseColor = colorScheme.bg === colorTheme.background ? 
                    (colorTheme.background === '#FFFFFF' ? '#FFFFFF' : colorTheme.background + 'F0') : 
                    colorScheme.bg;
                  
                  // Apply card background opacity
                  const opacity = content.cardBackgroundOpacity !== undefined ? content.cardBackgroundOpacity / 100 : 1;
                  
                  // Convert hex to rgba for opacity support
                  if (baseColor && baseColor.startsWith('#')) {
                    const hex = baseColor.replace('#', '');
                    // Handle both 3-digit and 6-digit hex codes
                    let r, g, b;
                    if (hex.length === 6) {
                      r = parseInt(hex.substr(0, 2), 16);
                      g = parseInt(hex.substr(2, 2), 16);
                      b = parseInt(hex.substr(4, 2), 16);
                    } else if (hex.length === 3) {
                      r = parseInt(hex.substr(0, 1) + hex.substr(0, 1), 16);
                      g = parseInt(hex.substr(1, 1) + hex.substr(1, 1), 16);
                      b = parseInt(hex.substr(2, 1) + hex.substr(2, 1), 16);
                    } else {
                      return baseColor; // Invalid hex, return as-is
                    }
                    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
                  }
                  
                  // For named colors or other formats, try to preserve opacity
                  if (opacity < 1 && baseColor) {
                    // For simple cases, we can try to convert common named colors
                    const namedColors = {
                      'white': 'rgba(255, 255, 255,',
                      'black': 'rgba(0, 0, 0,',
                      'red': 'rgba(255, 0, 0,',
                      'green': 'rgba(0, 128, 0,',
                      'blue': 'rgba(0, 0, 255,'
                    };
                    
                    for (const [name, rgba] of Object.entries(namedColors)) {
                      if (baseColor.toLowerCase().includes(name)) {
                        return `${rgba} ${opacity})`;
                      }
                    }
                  }
                  
                  return baseColor;
                })(),
                ...getImageStyle(),
                display: 'flex',
                flexDirection: 'column',
                textAlign: content.textAlign || 'left'
              }}
            >
              {/* Service Image */}
              {content.showImages !== false && (
                <div 
                  className="mb-4 bg-gray-100 flex items-center justify-center overflow-hidden"
                  style={{
                    ...getFrameStyle(),
                    ...getBorderStyle(),
                    backgroundColor: service.imageUrl ? 'transparent' : '#f3f4f6',
                    margin: content.imageFrame === 'circle' ? '2px' : '0'
                  }}
                >
                  {service.imageUrl ? (
                    <img 
                      src={resolveImageUrl(service.imageUrl)} 
                      alt={service.title || `Service ${index + 1}`}
                      className="w-full h-full object-cover"
                      style={{
                        borderRadius: content.imageFrame === 'circle' ? '50%' : '0'
                      }}
                      onError={(e) => {
                        console.error(`Failed to load service image: ${service.imageUrl}`, e);
                        if (e.target) {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling && e.target.nextSibling.style) {
                            e.target.nextSibling.style.display = 'flex';
                          }
                        }
                      }}
                      onLoad={() => {
                        
                      }}
                    />
                  ) : null}
                  {!service.imageUrl && (
                    <div className="text-gray-400 text-4xl">
                      📷
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex-1 p-4">
                {/* Service Title */}
                {content.showHeading !== false && (
                  <h3 
                    className="mb-3"
                    style={{ 
                      color: colorScheme.text,
                      ...getHeadingStyle(3),
                      textAlign: content.textAlign || 'left'
                    }}
                  >
                    {service.title || `Service ${index + 1}`}
                  </h3>
                )}
                
                {/* Service Description */}
                {content.showDescription !== false && (
                  <p style={{ 
                    color: colorScheme.text + (colorScheme.bg === '#ffffff' ? 'CC' : 'DD'),
                    ...getTextStyle(),
                    textAlign: content.textAlign || 'left'
                  }}>
                    {service.description || 'Professional service tailored to your needs'}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        
      </div>
    </section>
  );
};

// Gallery Section Component
const GallerySection = ({ section, colorTheme, designProps, getHeadingStyle, getImageStyle, getMaxWidthClass, selectedSection }) => {
  const content = section.content || {};
  const [modalOpen, setModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get gallery colors
  const galleryColors = content.colorScheme ? (() => {
    const themes = {
      light: { bg: '#f8f9fa', text: '#212529', accent: '#dc3545' },
      gray: { bg: '#e9ecef', text: '#495057', accent: '#dc3545' },
      blue: { bg: '#dee2e6', text: '#343a40', accent: '#dc3545' },
      dark1: { bg: '#343a40', text: '#ffffff', accent: '#ffffff' },
      dark2: { bg: '#212529', text: '#ffffff', accent: '#ffffff' },
      wine: { bg: '#4a0e0e', text: '#ffffff', accent: '#ffffff' }
    };
    return themes[content.colorTheme] || colorTheme;
  })() : colorTheme;
  
  // Use uploaded images or fallback to placeholders
  const images = content.images?.filter(img => img.url) || [
    { url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=400', caption: 'Sample Image 1' },
    { url: 'https://images.unsplash.com/photo-1551218808-94e220e084d2?w=400', caption: 'Sample Image 2' },
    { url: 'https://images.unsplash.com/photo-1559192398-c4dc3ba99b5b?w=400', caption: 'Sample Image 3' },
    { url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400', caption: 'Sample Image 4' }
  ];
  
  const openModal = (index) => {
    // Don't open modal if a section is selected (editing mode)
    if (selectedSection) return;
    
    setCurrentImageIndex(index);
    setModalOpen(true);
  };
  
  const closeModal = () => {
    setModalOpen(false);
  };
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  const getBorderStyle = () => {
    if (content.borders === 'all') {
      return { border: `2px solid ${galleryColors.accent}` };
    } else if (content.borders === 'bottom') {
      return { borderBottom: `2px solid ${galleryColors.accent}` };
    }
    return {};
  };
  
  const getFrameStyle = () => {
    return {
      borderRadius: '8px',
      ...getImageStyle()
    };
  };
  
  const getFrameAspectRatio = () => {
    const frame = content.frame || 'landscape';
    switch (frame) {
      case 'landscape': return '4/3';
      case 'portrait': return '3/4';
      case 'square': return '1/1';
      default: return '4/3';
    }
  };
  
  return (
    <>
      <section 
        className="py-20 px-6"
        style={{ 
          backgroundColor: galleryColors.bg,
          fontFamily: designProps.fontFamily 
        }}
      >
        <div className={`${getMaxWidthClass()} mx-auto`}>
          <div className="text-center mb-12">
            <h2 
              className="mb-4"
              style={{ 
                color: galleryColors.text,
                ...getHeadingStyle(2)
              }}
            >
              {content.title || 'Gallery'}
            </h2>
            {content.description && (
              <p 
                className="text-lg max-w-2xl mx-auto"
                style={{ 
                  color: galleryColors.text + '90',
                  fontFamily: designProps.fontFamily 
                }}
              >
                {content.description}
              </p>
            )}
          </div>
          
          {(() => {
            const layout = content.layout || 'uniform-grid';
            
            if (layout === 'carousel') {
              return (
                <div className="relative">
                  <div className="overflow-hidden">
                    <div 
                      className="flex transition-transform duration-300"
                      style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                    >
                      {images.map((image, index) => (
                        <div
                          key={index}
                          className="w-full flex-shrink-0 px-2"
                        >
                          <div
                            className="cursor-pointer relative overflow-hidden"
                            style={{
                              aspectRatio: getFrameAspectRatio(),
                              ...getFrameStyle(),
                              ...getBorderStyle()
                            }}
                            onClick={() => openModal(index)}
                          >
                            <img 
                              src={resolveImageUrl(image.url)} 
                              alt={image.caption || `Gallery image ${index + 1}`}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                            {image.caption && (
                              <div 
                                className="absolute bottom-0 left-0 right-0 p-2 text-sm"
                                style={{ 
                                  backgroundColor: 'rgba(0,0,0,0.7)',
                                  color: 'white'
                                }}
                              >
                                {image.caption}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full"
                        style={{ 
                          backgroundColor: galleryColors.accent,
                          color: galleryColors.bg
                        }}
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 rounded-full"
                        style={{ 
                          backgroundColor: galleryColors.accent,
                          color: galleryColors.bg
                        }}
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </div>
              );
            }
            
            if (layout === 'masonry-grid') {
              // Create masonry effect with CSS columns
              const isMobile = window.innerWidth < 640;
              const isTablet = window.innerWidth < 1024;
              
              return (
                <div 
                  style={{
                    columnCount: 'auto',
                    columnWidth: isMobile ? '100%' : isTablet ? '280px' : '300px',
                    columnGap: isMobile ? '12px' : '16px',
                    columnFill: 'balance'
                  }}
                  className="masonry-container"
                >
                  {images.map((image, index) => {
                    // Create varied heights for Pinterest-style masonry
                    const desktopHeights = [300, 400, 350, 500, 250, 450, 320, 480, 280, 380];
                    const mobileHeights = [200, 280, 240, 320, 180, 300, 220, 340, 190, 260];
                    const heights = isMobile ? mobileHeights : desktopHeights;
                    const randomHeight = heights[index % heights.length];
                    
                    return (
                      <div
                        key={index}
                        className="cursor-pointer relative overflow-hidden group"
                        style={{
                          breakInside: 'avoid',
                          display: 'inline-block',
                          width: '100%',
                          height: `${randomHeight}px`,
                          borderRadius: '12px',
                          marginBottom: isMobile ? '12px' : '16px',
                          ...getBorderStyle()
                        }}
                        onClick={() => openModal(index)}
                      >
                        <img 
                          src={resolveImageUrl(image.url)} 
                          alt={image.caption || `Gallery image ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          style={{
                            borderRadius: '12px'
                          }}
                        />
                        {image.caption && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 p-3 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{ 
                              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
                              color: 'white',
                              borderRadius: '0 0 12px 12px'
                            }}
                          >
                            {image.caption}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {images.length === 0 && (
                    <div className="text-center py-12" style={{ columnSpan: 'all' }}>
                      <Image className="w-12 h-12 mx-auto mb-4" style={{ color: galleryColors.text + '50' }} />
                      <p style={{ color: galleryColors.text + '80', fontFamily: designProps.fontFamily }}>
                        No images uploaded yet
                      </p>
                    </div>
                  )}
                </div>
              );
            }
            
            if (layout === 'lightbox') {
              return (
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {images.slice(0, 5).map((image, index) => (
                    <div
                      key={index}
                      className="cursor-pointer relative overflow-hidden group"
                      style={{
                        aspectRatio: '1/1',
                        ...getFrameStyle(),
                        ...getBorderStyle()
                      }}
                      onClick={() => openModal(index)}
                    >
                      <img 
                        src={resolveImageUrl(image.url)} 
                        alt={image.caption || `Gallery image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {image.caption && (
                        <div 
                          className="absolute bottom-0 left-0 right-0 p-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                          style={{ 
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            color: 'white'
                          }}
                        >
                          {image.caption}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {images.length > 5 && (
                    <div
                      className="cursor-pointer relative overflow-hidden group flex items-center justify-center"
                      style={{
                        aspectRatio: '1/1',
                        backgroundColor: galleryColors.accent + '20',
                        ...getFrameStyle(),
                        ...getBorderStyle()
                      }}
                      onClick={() => openModal(5)}
                    >
                      <div className="text-center">
                        <span 
                          className="text-2xl font-bold block"
                          style={{ color: galleryColors.accent }}
                        >
                          +{images.length - 5}
                        </span>
                        <span 
                          className="text-xs"
                          style={{ color: galleryColors.accent }}
                        >
                          more
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {images.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <Image className="w-12 h-12 mx-auto mb-4" style={{ color: galleryColors.text + '50' }} />
                      <p style={{ color: galleryColors.text + '80', fontFamily: designProps.fontFamily }}>
                        No images uploaded yet
                      </p>
                    </div>
                  )}
                </div>
              );
            }
            
            // Uniform Grid Layout (default)
            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="cursor-pointer relative overflow-hidden group"
                    style={{
                      aspectRatio: getFrameAspectRatio(),
                      ...getFrameStyle(),
                      ...getBorderStyle()
                    }}
                    onClick={() => openModal(index)}
                  >
                    <img 
                      src={resolveImageUrl(image.url)} 
                      alt={image.caption || `Gallery image ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {image.caption && (
                      <div 
                        className="absolute bottom-0 left-0 right-0 p-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ 
                          backgroundColor: 'rgba(0,0,0,0.7)',
                          color: 'white'
                        }}
                      >
                        {image.caption}
                      </div>
                    )}
                  </div>
                ))}
                
                {images.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <Image className="w-12 h-12 mx-auto mb-4" style={{ color: galleryColors.text + '50' }} />
                    <p style={{ color: galleryColors.text + '80', fontFamily: designProps.fontFamily }}>
                      No images uploaded yet
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </section>
      
      {/* Image Modal */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
          onClick={closeModal}
        >
          <div className="relative max-w-4xl max-h-full">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white'
              }}
            >
              <X className="w-6 h-6" />
            </button>
            
            <img 
              src={resolveImageUrl(images[currentImageIndex]?.url)} 
              alt={images[currentImageIndex]?.caption || `Gallery image ${currentImageIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {images[currentImageIndex]?.caption && (
              <div 
                className="absolute bottom-4 left-4 right-4 p-4 text-center"
                style={{ 
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  borderRadius: '8px'
                }}
              >
                {images[currentImageIndex].caption}
              </div>
            )}
            
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 rounded-full"
                  style={{ 
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white'
                  }}
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Testimonials Section Component
const TestimonialsSection = ({ section, colorTheme, designProps, getHeadingStyle, getImageStyle, getMaxWidthClass, getUnifiedColorScheme }) => {
  const content = section.content || {};
  const testimonials = content.testimonials || [
    {
      name: 'John Smith',
      text: 'Excellent service and professional team. Highly recommended!',
      rating: 5,
      company: 'ABC Corp',
      title: 'CEO',
      avatar: ''
    },
    {
      name: 'Sarah Johnson',
      text: 'Outstanding quality and attention to detail. Very satisfied!',
      rating: 5,
      company: 'XYZ Inc',
      title: 'Marketing Director',
      avatar: ''
    },
    {
      name: 'Mike Wilson',
      text: 'Professional, reliable, and delivered exactly what we needed.',
      rating: 5,
      company: 'Tech Solutions',
      title: 'CTO',
      avatar: ''
    }
  ];
  
  // Get unified color scheme (supports custom themes) - use content.colorScheme for consistency with section editors
  const testimonialColors = getUnifiedColorScheme ? 
    (() => {
      const colors = getUnifiedColorScheme(content.colorScheme || 'default');
      // Add cardBg property for testimonials based on scheme type
      let cardBg = '#ffffff';
      const scheme = content.colorScheme || 'default';
      
      if (scheme === 'light') {
        cardBg = '#ffffff';
      } else if (scheme === 'primary') {
        cardBg = '#ffffff';
      } else if (scheme === 'dark') {
        cardBg = '#374151';
      } else if (scheme === 'white') {
        cardBg = '#f9fafb';
      } else {
        // For COLOR_THEMES and custom themes, use contrasting background
        cardBg = colors.bg === '#ffffff' ? '#f9fafb' : '#ffffff';
      }
      
      return {
        ...colors,
        cardBg: cardBg
      };
    })() : {
      bg: '#f9fafb',
      text: colorTheme.text,
      border: colorTheme.accent,
      accent: colorTheme.primary,
      secondary: colorTheme.secondary,
      cardBg: '#ffffff'
    };
  
  // Debug logging for testimonials section color resolution
  console.log('🎨 [TESTIMONIALS-SECTION] Color resolution:', {
    'content.colorScheme': content.colorScheme,
    'resolved testimonialColors': testimonialColors,
    'colorTheme': colorTheme,
    'getUnifiedColorScheme available': !!getUnifiedColorScheme
  });
  
  // Get layout classes
  const getLayoutClasses = () => {
    const columns = content.columns || '3';
    switch (columns) {
      case '1':
        return 'grid grid-cols-1 gap-8';
      case '2':
        return 'grid grid-cols-1 md:grid-cols-2 gap-8';
      case '3':
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8';
    }
  };
  
  // Get card style
  const getCardStyle = () => {
    const cardStyle = content.cardStyle || 'default';
    const baseStyle = {
      backgroundColor: testimonialColors.cardBg,
      ...getImageStyle()
    };
    
    switch (cardStyle) {
      case 'bordered':
        return { ...baseStyle, border: `2px solid ${colorTheme.primary}30`, boxShadow: 'none' };
      case 'shadow':
        return { ...baseStyle, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' };
      case 'minimal':
        return { ...baseStyle, boxShadow: 'none', backgroundColor: 'transparent' };
      default: // default
        return { ...baseStyle, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' };
    }
  };
  
  // Get star display
  const renderStars = (rating) => {
    if (content.showRatings === false) return null;
    
    const starStyle = content.starStyle || 'filled';
    const stars = [];
    
    for (let i = 0; i < 5; i++) {
      if (starStyle === 'emoji') {
        stars.push(
          <span key={i} style={{ fontSize: '18px' }}>
            {i < rating ? '⭐' : '☆'}
          </span>
        );
      } else if (starStyle === 'outlined') {
        stars.push(
          <span key={i} style={{ color: colorTheme.accent, fontSize: '18px' }}>
            {i < rating ? '★' : '☆'}
          </span>
        );
      } else {
        stars.push(
          <Star key={i} className="w-5 h-5 fill-current" style={{ color: i < rating ? colorTheme.accent : '#d1d5db' }} />
        );
      }
    }
    
    return <div className="flex mb-4">{stars}</div>;
  };
  
  // Get text alignment
  const getTextAlignment = () => {
    return content.textAlign || 'left';
  };

  // Get avatar style
  const getAvatarStyle = () => {
    const avatarStyle = content.avatarStyle || 'circle';
    switch (avatarStyle) {
      case 'square':
        return 'rounded-none';
      case 'rounded':
        return 'rounded-lg';
      case 'circle':
      default:
        return 'rounded-full';
    }
  };
  
  return (
    <section 
      className="py-20 px-6"
      style={{ 
        backgroundColor: testimonialColors.bg,
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto`}>
        {content.showHeading !== false && (
          <div className="mb-12" style={{ textAlign: getTextAlignment() }}>
            <h2 
              className="mb-4"
              style={{ 
                color: testimonialColors.text,
                ...getHeadingStyle(2),
                textAlign: getTextAlignment()
              }}
            >
              {content.title || 'What Our Clients Say'}
            </h2>
          </div>
        )}
        
        <div className={getLayoutClasses()}>
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className={`p-6 ${content.layout === 'masonry' ? 'break-inside-avoid mb-6' : ''}`}
              style={{ 
                ...getCardStyle(),
                textAlign: getTextAlignment()
              }}
            >
              {/* Avatar */}
              {content.showAvatars && testimonial.avatar && (
                <div className="mb-4" style={{ textAlign: getTextAlignment() }}>
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name}
                    className={`w-16 h-16 ${getAvatarStyle()} object-cover`}
                    style={{ 
                      marginLeft: getTextAlignment() === 'center' ? 'auto' : getTextAlignment() === 'right' ? 'auto' : '0',
                      marginRight: getTextAlignment() === 'center' ? 'auto' : getTextAlignment() === 'right' ? '0' : 'auto'
                    }}
                  />
                </div>
              )}
              
              {/* Stars */}
              <div style={{ textAlign: getTextAlignment() }}>
                {renderStars(testimonial.rating)}
              </div>
              
              {/* Quote */}
              <p 
                className="mb-4"
                style={{ 
                  color: testimonialColors.text + (testimonialColors.bg === '#ffffff' ? 'DD' : 'CC'),
                  fontFamily: designProps.fontFamily,
                  fontStyle: content.showQuotes !== false ? 'italic' : 'normal',
                  textAlign: getTextAlignment()
                }}
              >
                {content.showQuotes !== false ? `"${testimonial.text}"` : testimonial.text}
              </p>
              
              {/* Name */}
              <div style={{ textAlign: getTextAlignment() }}>
                <p 
                  className="font-semibold"
                  style={{ 
                    color: testimonialColors.text,
                    fontFamily: designProps.fontFamily,
                    marginBottom: content.showCompany && (testimonial.company || testimonial.title) ? '4px' : '0'
                  }}
                >
                  {testimonial.name}
                </p>
                
                {/* Company and Title */}
                {content.showCompany && (testimonial.company || testimonial.title) && (
                  <p 
                    className="text-sm"
                    style={{ 
                      color: testimonialColors.text + '80',
                      fontFamily: designProps.fontFamily
                    }}
                  >
                    {[testimonial.title, testimonial.company].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getButtonStyle, getMaxWidthClass, getUnifiedColorScheme }) => {
  const content = section.content || {};
  
  // Get unified color scheme (supports custom themes) - use content.colorTheme for consistency with HTML generator
  const contactColors = getUnifiedColorScheme ? getUnifiedColorScheme(content.colorScheme || 'default') : {
    bg: '#f9fafb',
    text: colorTheme.text,
    border: colorTheme.accent,
    accent: colorTheme.primary,
    secondary: colorTheme.secondary
  };
  
  // Debug logging for contact section color resolution
  console.log('🎨 [CONTACT-SECTION] Color resolution:', {
    'content.colorScheme': content.colorScheme,
    'resolved contactColors': contactColors,
    'colorTheme': colorTheme,
    'getUnifiedColorScheme available': !!getUnifiedColorScheme
  });
  
  // Get layout classes
  const getLayoutClasses = () => {
    const layout = content.layout || 'split';
    switch (layout) {
      case 'stacked':
        return 'flex flex-col space-y-8';
      case 'info-only':
        return 'flex justify-center';
      case 'form-only':
        return 'flex justify-center';
      default: // split
        return 'grid grid-cols-1 md:grid-cols-2 gap-12';
    }
  };
  
  // Get text alignment
  const getTextAlignment = () => {
    return content.textAlign || 'left';
  };
  
  // Contact info component
  const ContactInfo = () => {
    if (content.showContactInfo === false) return null;
    
    return (
      <div className="space-y-6" style={{ textAlign: getTextAlignment() }}>
        {/* Phone */}
        {content.phone && (
          <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
            <Phone className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
            <span style={{ 
              color: contactColors.text,
              fontFamily: designProps.fontFamily
            }}>{content.phone}</span>
          </div>
        )}
        
        {/* Email */}
        {content.email && (
          <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
            <Mail className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
            <span style={{ 
              color: contactColors.text,
              fontFamily: designProps.fontFamily
            }}>{content.email}</span>
          </div>
        )}
        
        {/* Address */}
        {content.address && (
          <div className="flex items-start" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
            <MapPin className="w-6 h-6 mr-4 mt-1" style={{ color: colorTheme.primary }} />
            <span style={{ 
              color: contactColors.text,
              fontFamily: designProps.fontFamily,
              whiteSpace: 'pre-line'
            }}>{content.address}</span>
          </div>
        )}
        
        {/* Website */}
        {content.website && (
          <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
            <ExternalLink className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
            <a 
              href={content.website}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ 
                color: contactColors.text,
                fontFamily: designProps.fontFamily
              }}
            >
              {content.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
        
        {/* Fallback to business info if no custom contact info */}
        {!content.phone && !content.email && !content.address && !content.website && (
          <>
            {businessInfo.location && (
              <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
                <MapPin className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
                <span style={{ 
                  color: contactColors.text,
                  fontFamily: designProps.fontFamily
                }}>{businessInfo.location}</span>
              </div>
            )}
            
            <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
              <Phone className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
              <span style={{ 
                color: contactColors.text,
                fontFamily: designProps.fontFamily
              }}>+1 (555) 123-4567</span>
            </div>
            
            <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
              <Mail className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
              <span style={{ 
                color: contactColors.text,
                fontFamily: designProps.fontFamily
              }}>hello@{businessInfo.name?.toLowerCase().replace(/\s+/g, '') || 'business'}.com</span>
            </div>

            {businessInfo.existingWebLink && (
              <div className="flex items-center" style={{ justifyContent: getTextAlignment() === 'center' ? 'center' : getTextAlignment() === 'right' ? 'flex-end' : 'flex-start' }}>
                <ExternalLink className="w-6 h-6 mr-4" style={{ color: colorTheme.primary }} />
                <a 
                  href={businessInfo.existingWebLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ 
                    color: contactColors.text,
                    fontFamily: designProps.fontFamily
                  }}
                >
                  Visit Our Website
                </a>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  
  // Contact form component
  const ContactForm = () => {
    if (content.showContactForm === false) return null;
    
    const formFields = content.formFields || {};
    
    return (
      <div className="space-y-4">
        <form className="space-y-4" action={content.formAction || '#'} method="POST">
          {formFields.name !== false && (
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ 
                fontFamily: designProps.fontFamily,
                textAlign: getTextAlignment()
              }}
            />
          )}
          
          {formFields.email !== false && (
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ 
                fontFamily: designProps.fontFamily,
                textAlign: getTextAlignment()
              }}
            />
          )}
          
          {formFields.subject !== false && (
            <input
              type="text"
              name="subject"
              placeholder="Subject"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ 
                fontFamily: designProps.fontFamily,
                textAlign: getTextAlignment()
              }}
            />
          )}
          
          {formFields.message !== false && (
            <textarea
              name="message"
              rows={5}
              placeholder="Your Message"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              style={{ 
                fontFamily: designProps.fontFamily,
                textAlign: getTextAlignment()
              }}
            />
          )}
          
          {content.showButton !== false && (
            <button
              type="submit"
              className="w-full px-8 py-4 font-semibold text-lg transition hover:opacity-90"
              style={{
                ...getButtonStyle(true),
                fontSize: '18px',
                textAlign: 'center'
              }}
            >
              {content.ctaText || 'Send Message'}
            </button>
          )}
        </form>
      </div>
    );
  };
  
  return (
    <section 
      className="py-20 px-6"
      style={{ 
        backgroundColor: contactColors.bg,
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto`}>
        {/* Header */}
        {(content.showHeading !== false || content.showDescription !== false) && (
          <div className="mb-12" style={{ textAlign: getTextAlignment() }}>
            {content.showHeading !== false && (
              <h2 
                className="mb-4"
                style={{ 
                  color: contactColors.text,
                  ...getHeadingStyle(2),
                  textAlign: getTextAlignment()
                }}
              >
                {content.title || 'Get In Touch'}
              </h2>
            )}
            
            {content.showDescription !== false && (
              <p 
                className="text-lg"
                style={{ 
                  color: contactColors.text + (contactColors.bg === '#ffffff' ? 'DD' : 'CC'),
                  fontFamily: designProps.fontFamily,
                  textAlign: getTextAlignment()
                }}
              >
                {content.description || 'Ready to work with us? Contact us today!'}
              </p>
            )}
          </div>
        )}
        
        {/* Content Layout */}
        <div className={getLayoutClasses()}>
          {/* Show contact info unless layout is form-only */}
          {content.layout !== 'form-only' && <ContactInfo />}
          
          {/* Show contact form unless layout is info-only */}
          {content.layout !== 'info-only' && <ContactForm />}
          
          {/* Simple button for info-only layout */}
          {content.layout === 'info-only' && content.showButton !== false && (
            <div className="mt-8" style={{ textAlign: getTextAlignment() }}>
              <button
                className="px-8 py-4 font-semibold text-lg transition hover:opacity-90"
                style={{
                  ...getButtonStyle(true),
                  fontSize: '18px'
                }}
              >
                {content.ctaText || 'Contact Us'}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Newsletter Section Component
const NewsletterSection = ({ section, colorTheme, designProps, getHeadingStyle, getButtonStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  
  return (
    <section 
      className="py-20 px-6"
      style={{ 
        backgroundColor: colorTheme.primary,
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto text-center`}>
        <h2 
          className="mb-4"
          style={{ 
            ...getHeadingStyle(2),
            color: colorTheme.background
          }}
        >
          {content.title || 'Stay Updated'}
        </h2>
        <p 
          className="text-lg mb-8"
          style={{ 
            color: colorTheme.background + 'DD',
            fontFamily: designProps.fontFamily
          }}
        >
          {content.description || 'Subscribe to our newsletter for the latest updates and offers.'}
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <input
            type="email"
            placeholder="Enter your email"
            className="flex-1 px-4 py-3 border-0 focus:ring-2 focus:ring-white"
            style={{ 
              backgroundColor: colorTheme.background, 
              color: colorTheme.text,
              fontFamily: designProps.fontFamily,
              borderRadius: getButtonStyle().borderRadius
            }}
          />
          <button
            className="px-6 py-3 font-semibold border-2 transition hover:opacity-90"
            style={{
              borderColor: colorTheme.background,
              color: colorTheme.background,
              backgroundColor: 'transparent',
              fontFamily: designProps.fontFamily,
              borderRadius: getButtonStyle().borderRadius
            }}
          >
            Subscribe
          </button>
        </div>
      </div>
    </section>
  );
};

// Footer Section Component
const FooterSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  
  // Get section-specific colors if available, otherwise use site theme
  const getUnifiedColorScheme = (schemeName) => {
    if (!schemeName || schemeName === 'default') {
      return colorTheme;
    }
    
    // Import and use the same color themes as the editor
    const { COLOR_THEMES } = require('../constants/colorThemes');
    const selectedTheme = COLOR_THEMES.find(theme => theme.name === schemeName);
    
    if (selectedTheme) {
      return {
        primary: selectedTheme.colors?.primary || selectedTheme.primary,
        secondary: selectedTheme.colors?.secondary || selectedTheme.secondary,
        accent: selectedTheme.colors?.accent || selectedTheme.accent,
        background: selectedTheme.colors?.background || selectedTheme.background,
        text: selectedTheme.colors?.text || selectedTheme.text
      };
    }
    
    return colorTheme;
  };

  const sectionColorScheme = getUnifiedColorScheme(content.colorScheme || 'default');

  // Get color scheme with inverted colors support (matches HTML Generator logic)
  const getFooterColors = () => {
    const colors = {
      bg: sectionColorScheme.background,
      text: sectionColorScheme.text,
      border: sectionColorScheme.accent,
      accent: sectionColorScheme.primary
    };
    
    // Apply inverted colors if enabled
    if (content.invertedColors) {
      return {
        bg: colors.text,
        text: colors.bg,
        accent: colors.accent,
        border: colors.accent
      };
    }
    
    return colors;
  };
  
  const footerColors = getFooterColors();
  
  // Get layout classes
  const getLayoutClasses = () => {
    const layout = content.layout || 'centered';
    switch (layout) {
      case 'columns':
        return 'grid grid-cols-1 md:grid-cols-3 gap-8';
      case 'left-aligned':
        return 'text-left';
      case 'minimal':
        return 'text-center space-y-2';
      default: // centered
        return 'text-center space-y-6';
    }
  };
  
  // Social media icons
  const getSocialIcon = (platform) => {
    const icons = {
      facebook: '📘',
      twitter: '🐦',
      instagram: '📷',
      linkedin: '💼',
      youtube: '📺'
    };
    return icons[platform] || '🔗';
  };
  
  return (
    <footer 
      className="py-12 px-6 border-t w-full"
      style={{ 
        backgroundColor: footerColors.bg,
        borderColor: footerColors.accent + '30',
        fontFamily: designProps.fontFamily 
      }}
    >
      <div className={`${getMaxWidthClass()} mx-auto`}>
        <div className={getLayoutClasses()}>
          
            {/* Quick Links Section */}
            {content.showQuickLinks !== false && content.quickLinks && content.quickLinks.length > 0 && (
            <div className={`${content.layout === 'columns' ? 'space-y-3' : 'space-y-2'}`}>
              
              <div className={`${content.layout === 'columns' ? 'space-y-2' : 'flex flex-wrap gap-4 justify-center'}`}>
                {content.quickLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url || '#'}
                    className="text-sm transition hover:opacity-70"
                    style={{ 
                      color: footerColors.text,
                      opacity: 0.8,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {link.text || 'Link'}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Contact Info Section */}
          {content.showContactInfo !== false && (content.email || content.phone || content.address) && (
            <div className={`${content.layout === 'columns' ? 'space-y-2' : 'space-y-1'}`}>
              
              <div className="flex items-center justify-center flex-wrap gap-4 text-xs" style={{ color: footerColors.text, opacity: 0.8 }}>
                {content.phone && (
                  <div className="flex items-center">
                    <span className="mr-1">📞</span>
                    {content.phone}
                  </div>
                )}
                
                {content.email && (
                  <div className="flex items-center">
                    <span className="mr-1">✉️</span>
                    {content.email}
                  </div>
                )}
                
                {content.address && (
                  <div className="flex items-center">
                    <span className="mr-1">📍</span>
                    <span>{content.address}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          

          
          {/* Social Media Section */}
          {content.showSocialLinks !== false && content.socialLinks && content.socialLinks.length > 0 && (
            <div className={`${content.layout === 'columns' ? 'space-y-2' : 'space-y-3'}`}>
              <h4 
                className="font-medium mb-2"
                style={{ 
                  color: footerColors.text,
                  fontSize: '14px'
                }}
              >
                Follow Us
              </h4>
              
              <div className="flex gap-3 justify-center">
                {content.socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-lg transition hover:opacity-70"
                    style={{ 
                      backgroundColor: footerColors.accent + '20',
                      color: footerColors.text
                    }}
                    title={social.platform}
                  >
                    {getSocialIcon(social.platform)}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Copyright Section */}
        {content.showCopyright !== false && (
          <div 
            className={`pt-8 mt-8 border-t text-xs ${content.layout === 'minimal' ? 'text-center' : 'text-center'}`}
            style={{ 
              borderColor: footerColors.accent + '20'
            }}
          >
            <div 
              style={{ 
                color: footerColors.text, 
                opacity: 0.6,
                fontFamily: designProps.fontFamily
              }}
            >
              {content.copyrightText || `© ${new Date().getFullYear()} ${content.companyName || businessInfo?.name || 'Your Business'}. All rights reserved.`}
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

// Team Section Component
const TeamSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getImageStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  const members = content.members || [];
  const layout = content.layout || {};
  
  // Get team color scheme (similar to other sections) - use content.colorTheme for consistency with HTML generator
  const getTeamColors = () => {
    const scheme = content.colorScheme || 'default';
    switch (scheme) {
      case 'dark':
        return { bg: '#111827', text: '#f9fafb', accent: colorTheme.primary };
      case 'primary':
        return { bg: colorTheme.primary, text: colorTheme.background, accent: colorTheme.background };
      case 'light':
        return { bg: '#f9fafb', text: colorTheme.text, accent: colorTheme.primary };
      case 'accent':
        return { bg: colorTheme.accent, text: '#ffffff', accent: colorTheme.primary };
      case 'custom':
        return { bg: colorTheme.secondary || colorTheme.background, text: colorTheme.text, accent: colorTheme.primary };
      default:
        return { bg: colorTheme.background, text: colorTheme.text, accent: colorTheme.accent };
    }
  };
  
  const teamColors = getTeamColors();
  
  if (members.length === 0) {
    return (
      <div className="py-16 text-center" style={{ backgroundColor: teamColors.bg }}>
        <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
          <p style={{ color: teamColors.text, opacity: 0.6 }}>No team members added yet</p>
        </div>
      </div>
    );
  }

  // Layout configuration
  const columns = layout.columns || 'auto';
  const cardStyle = layout.cardStyle || 'simple';
  const imageStyle = layout.imageStyle || 'circle';
  const imageSize = layout.imageSize || 'medium';
  const textAlign = layout.textAlign || 'center';
  
  // Get grid classes based on layout
  const getGridClasses = () => {
    if (columns === 'auto') {
      return `grid gap-8 ${
        members.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
        members.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
        members.length === 3 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
        'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      }`;
    } else {
      const numCols = parseInt(columns);
      switch (numCols) {
        case 1:
          return 'grid gap-8 grid-cols-1 max-w-md mx-auto';
        case 2:
          return 'grid gap-8 grid-cols-1 md:grid-cols-2';
        case 3:
          return 'grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
        case 4:
          return 'grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
        default:
          return 'grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      }
    }
  };
  
  // Get image classes and styles
  const getImageClasses = () => {
    const sizeClasses = {
      small: 'w-24 h-24',
      medium: 'w-32 h-32',
      large: 'w-40 h-40'
    };
    
    const shapeClasses = {
      circle: 'rounded-full',
      square: 'rounded-none',
      rounded: 'rounded-lg'
    };
    
    return `${sizeClasses[imageSize]} ${shapeClasses[imageStyle]} mx-auto object-cover`;
  };
  
  // Get card classes based on style
  const getCardClasses = () => {
    const alignClass = textAlign === 'center' ? 'text-center' : textAlign === 'right' ? 'text-right' : 'text-left';
    
    switch (cardStyle) {
      case 'card':
        return `${alignClass} p-6 rounded-lg`;
      case 'bordered':
        return `${alignClass} p-6 border-2 rounded-lg`;
      case 'shadow':
        return `${alignClass} p-6 rounded-lg shadow-lg`;
      default:
        return alignClass;
    }
  };
  
  // Get card style object
  const getCardStyle = () => {
    if (cardStyle === 'simple') return {};
    
    const baseStyle = {
      backgroundColor: teamColors.bg,
    };
    
    if (cardStyle === 'bordered') {
      baseStyle.borderColor = `${teamColors.accent}20`;
    }
    
    return baseStyle;
  };
  
  // Get specialty alignment classes
  const getSpecialtyAlignment = () => {
    switch (textAlign) {
      case 'left': return 'justify-start';
      case 'right': return 'justify-end';
      case 'center':
      default: return 'justify-center';
    }
  };

  return (
    <section className="py-16" style={{ backgroundColor: teamColors.bg }}>
      <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
        {(content.title || content.subtitle) && (
          <div className="text-center mb-12">
            {content.title && (
              <h2 
                className="font-bold mb-4"
                style={{ 
                  ...getHeadingStyle(2),
                  color: teamColors.text 
                }}
              >
                {content.title}
              </h2>
            )}
            {content.subtitle && (
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: teamColors.text, opacity: 0.8 }}
              >
                {content.subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className={getGridClasses()}>
          {members.map((member, index) => (
            <div key={index} className={getCardClasses()} style={getCardStyle()}>
              <div className="mb-4">
                <img
                  src={resolveImageUrl(member.imageUrl || '/uploads/placeholder-person.jpg')}
                  alt={member.name || 'Team Member'}
                  className={getImageClasses()}
                  style={{
                    border: `4px solid ${teamColors.accent}`,
                    ...getImageStyle()
                  }}
                />
              </div>
              <h3 
                className="text-xl font-semibold mb-1"
                style={{ color: teamColors.text }}
              >
                {member.name || 'Team Member'}
              </h3>
              <p 
                className="text-lg mb-3"
                style={{ color: teamColors.accent }}
              >
                {member.role || 'Team Member'}
              </p>
              {member.description && (
                <p 
                  className="text-sm mb-3"
                  style={{ color: teamColors.text, opacity: 0.8 }}
                >
                  {member.description}
                </p>
              )}
              {member.specialties && member.specialties.length > 0 && (
                <div className={`flex flex-wrap gap-2 ${getSpecialtyAlignment()}`}>
                  {member.specialties.map((specialty, specialtyIndex) => (
                    specialty && (
                      <span
                        key={specialtyIndex}
                        className="inline-block px-3 py-1 text-xs rounded-full"
                        style={{
                          backgroundColor: teamColors.accent,
                          color: teamColors.bg
                        }}
                      >
                        {specialty}
                      </span>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section Component
const FeaturesSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  const features = content.features || [];
  
  if (features.length === 0) {
    return (
      <div className="py-16 text-center" style={{ backgroundColor: colorTheme.background }}>
        <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
          <p style={{ color: colorTheme.text, opacity: 0.6 }}>No features added yet</p>
        </div>
      </div>
    );
  }

  const getIconHtml = (iconName) => {
    const iconMap = {
      'star': '⭐',
      'check': '✓',
      'heart': '♥',
      'lightning': '⚡',
      'shield': '🛡️',
      'target': '🎯',
      'rocket': '🚀',
      'diamond': '💎',
      'gear': '⚙️',
      'trophy': '🏆'
    };
    return iconMap[iconName] || '⭐';
  };

  return (
    <section className="py-16" style={{ backgroundColor: colorTheme.background }}>
      <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
        {(content.title || content.subtitle) && (
          <div className="text-center mb-12">
            {content.title && (
              <h2 
                className="font-bold mb-4"
                style={{ 
                  ...getHeadingStyle(2),
                  color: colorTheme.text 
                }}
              >
                {content.title}
              </h2>
            )}
            {content.subtitle && (
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: colorTheme.text, opacity: 0.8 }}
              >
                {content.subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className={`grid gap-8 ${
          features.length === 1 ? 'grid-cols-1 max-w-md mx-auto' :
          features.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="p-6 rounded-lg text-center transition-transform hover:scale-105"
              style={{
                backgroundColor: colorTheme.background,
                border: `2px solid ${colorTheme.accent}`,
                borderRadius: designProps.buttonCorners === 'square' ? '0px' : 
                            designProps.buttonCorners === 'soft' ? '8px' : 
                            designProps.buttonCorners === 'round' ? '16px' : '32px'
              }}
            >
              <div className="text-4xl mb-4">
                {getIconHtml(feature.icon)}
              </div>
              <h3 
                className="text-xl font-semibold mb-3"
                style={{ color: colorTheme.text }}
              >
                {feature.title || 'Feature'}
              </h3>
              {feature.description && (
                <p 
                  className="text-sm mb-4"
                  style={{ color: colorTheme.text, opacity: 0.8 }}
                >
                  {feature.description}
                </p>
              )}
              {feature.benefits && feature.benefits.length > 0 && (
                <ul className="text-sm space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    benefit && (
                      <li 
                        key={benefitIndex}
                        className="flex items-center justify-center"
                        style={{ color: colorTheme.primary }}
                      >
                        <span className="mr-2">✓</span>
                        {benefit}
                      </li>
                    )
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section Component
const PricingSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  const plans = content.plans || [];
  
  if (plans.length === 0) {
    return (
      <div className="py-16 text-center" style={{ backgroundColor: colorTheme.background }}>
        <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
          <p style={{ color: colorTheme.text, opacity: 0.6 }}>No pricing plans added yet</p>
        </div>
      </div>
    );
  }

  return (
    <section className="py-16" style={{ backgroundColor: colorTheme.background }}>
      <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
        {(content.title || content.subtitle) && (
          <div className="text-center mb-12">
            {content.title && (
              <h2 
                className="font-bold mb-4"
                style={{ 
                  ...getHeadingStyle(2),
                  color: colorTheme.text 
                }}
              >
                {content.title}
              </h2>
            )}
            {content.subtitle && (
              <p 
                className="text-xl max-w-3xl mx-auto"
                style={{ color: colorTheme.text, opacity: 0.8 }}
              >
                {content.subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className={`grid gap-8 max-w-5xl mx-auto ${
          plans.length === 1 ? 'grid-cols-1 max-w-md' :
          plans.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
          'grid-cols-1 md:grid-cols-3'
        }`}>
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className={`p-8 rounded-lg text-center relative transition-transform hover:scale-105 ${
                plan.popular ? 'scale-105 z-10' : ''
              }`}
              style={{
                backgroundColor: plan.popular ? colorTheme.primary : colorTheme.background,
                border: `3px solid ${plan.popular ? colorTheme.primary : colorTheme.accent}`,
                color: plan.popular ? colorTheme.background : colorTheme.text,
                borderRadius: designProps.buttonCorners === 'square' ? '0px' : 
                            designProps.buttonCorners === 'soft' ? '8px' : 
                            designProps.buttonCorners === 'round' ? '16px' : '32px'
              }}
            >
              {plan.popular && (
                <div 
                  className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: colorTheme.accent,
                    color: colorTheme.background
                  }}
                >
                  Most Popular
                </div>
              )}
              
              <h3 className="text-2xl font-bold mb-4">
                {plan.name || 'Plan'}
              </h3>
              
              <div className="mb-6">
                <span className="text-4xl font-bold">
                  {plan.price || '$0'}
                </span>
                <span className="text-lg opacity-80 ml-1">
                  {plan.period || 'per month'}
                </span>
              </div>
              
              {plan.description && (
                <p className="text-sm mb-6 opacity-90">
                  {plan.description}
                </p>
              )}
              
              {plan.features && plan.features.length > 0 && (
                <ul className="mb-8 space-y-3 text-sm">
                  {plan.features.map((feature, featureIndex) => (
                    feature && (
                      <li key={featureIndex} className="flex items-center justify-center">
                        <span className="mr-2">✓</span>
                        {feature}
                      </li>
                    )
                  ))}
                </ul>
              )}
              
              <button 
                className="w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: plan.popular ? colorTheme.background : colorTheme.primary,
                  color: plan.popular ? colorTheme.primary : colorTheme.background,
                  borderRadius: designProps.buttonCorners === 'square' ? '0px' : 
                              designProps.buttonCorners === 'soft' ? '4px' : 
                              designProps.buttonCorners === 'round' ? '8px' : '16px'
                }}
              >
                {plan.ctaText || 'Get Started'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section Component  
const CTASection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  const primaryCta = content.primaryCta || {};
  const secondaryCta = content.secondaryCta || {};

  return (
    <section 
      className="py-20 text-center relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colorTheme.primary} 0%, ${colorTheme.secondary} 100%)`
      }}
    >
      <div className={`mx-auto px-4 relative z-10 ${getMaxWidthClass()}`}>
        {content.title && (
          <h2 
            className="font-bold mb-4"
            style={{ 
              ...getHeadingStyle(1),
              color: colorTheme.background,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            {content.title}
          </h2>
        )}
        
        {content.subtitle && (
          <p 
            className="text-xl md:text-2xl mb-6"
            style={{ 
              color: colorTheme.background, 
              opacity: 0.9,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}
          >
            {content.subtitle}
          </p>
        )}
        
        {content.description && (
          <p 
            className="text-lg mb-8 max-w-3xl mx-auto"
            style={{ 
              color: colorTheme.background, 
              opacity: 0.8,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
            }}
          >
            {content.description}
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {primaryCta.text && (
            <button 
              className="px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: colorTheme.background,
                color: colorTheme.primary,
                borderRadius: designProps.buttonCorners === 'square' ? '0px' : 
                            designProps.buttonCorners === 'soft' ? '8px' : 
                            designProps.buttonCorners === 'round' ? '16px' : '32px',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
              }}
            >
              {primaryCta.text}
            </button>
          )}
          
          {secondaryCta.text && (
            <button 
              className="px-8 py-4 rounded-lg font-semibold text-lg border-2 transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: 'transparent',
                color: colorTheme.background,
                borderColor: colorTheme.background,
                borderRadius: designProps.buttonCorners === 'square' ? '0px' : 
                            designProps.buttonCorners === 'soft' ? '8px' : 
                            designProps.buttonCorners === 'round' ? '16px' : '32px'
              }}
            >
              {secondaryCta.text}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

// Article Section Component
const IframeSection = ({ section, colorTheme, designProps, getMaxWidthClass }) => {
  const { src, width, height, title, allowFullscreen, responsive, border, loading } = section.content || {};

  if (!src) {
    return (
      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">🖥️</div>
          <h3 className="text-lg font-medium mb-2">Iframe Section</h3>
          <p className="text-sm">Configure the source URL to display embedded content</p>
        </div>
      </div>
    );
  }

  const iframeStyle = {
    width: responsive ? '100%' : width,
    height: height,
    border: border,
    maxWidth: '100%'
  };

  const containerClasses = responsive 
    ? "relative w-full overflow-hidden" 
    : "block";

  return (
    <section className={`py-8 ${getMaxWidthClass()}`} style={{ fontFamily: designProps.fontFamily }}>
      <div className="container mx-auto px-4">
        <div className={containerClasses}>
          <iframe
            src={src}
            title={title || "Embedded content"}
            style={iframeStyle}
            allowFullScreen={allowFullscreen}
            loading={loading}
            className="rounded-md"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          />
        </div>
      </div>
    </section>
  );
};

// Custom Code Section Component
const CustomCodeSection = ({ section, colorTheme, designProps, getMaxWidthClass }) => {
  const content = section.content || {};
  
  // Check if the section is enabled
  if (content.enabled === false) {
    return (
      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">⏸️</div>
          <h3 className="text-lg font-medium mb-2">Custom Code Section (Disabled)</h3>
          <p className="text-sm">This section is disabled and won't display in live mode</p>
        </div>
      </div>
    );
  }

  // Check if any code is provided
  const hasCode = content.htmlCode || content.cssCode || content.jsCode;
  if (!hasCode) {
    return (
      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">⚡</div>
          <h3 className="text-lg font-medium mb-2">Custom Code Section</h3>
          <p className="text-sm">Add HTML, CSS, or JavaScript code to display custom content</p>
        </div>
      </div>
    );
  }

  // Generate unique ID for this section to scope CSS
  const sectionId = `custom-code-${section.id}`;
  
  // Process CSS based on scope setting
  const getScopedCSS = (cssCode) => {
    if (!cssCode) return '';
    
    const cssScope = content.cssScope || 'scoped';
    
    switch (cssScope) {
      case 'global':
        // Global CSS - no scoping
        return cssCode;
      
      case 'inline':
        // Inline CSS will be handled differently
        return '';
      
      case 'scoped':
      default:
        // Scoped CSS - wrap all rules with the section ID
        try {
          // Simple CSS scoping - prepend section ID to all selectors
          return cssCode.replace(/([^{}]+)\s*\{/g, (match, selector) => {
            // Skip @media, @keyframes, etc.
            if (selector.trim().startsWith('@')) {
              return match;
            }
            // Scope the selector to this section
            const scopedSelector = selector.split(',').map(sel => 
              `#${sectionId} ${sel.trim()}`
            ).join(', ');
            return `${scopedSelector} {`;
          });
        } catch (e) {
          console.warn('CSS scoping failed, applying as-is:', e);
          return cssCode;
        }
    }
  };

  // For invisible sections, we still need to render the code but not take up space
  const sectionStyle = {
    fontFamily: designProps.fontFamily,
    ...(content.isVisible === false ? { 
      height: 0, 
      overflow: 'hidden',
      padding: 0,
      margin: 0
    } : {})
  };

  const containerClasses = content.isVisible === false 
    ? '' 
    : `py-8 ${getMaxWidthClass()}`;

  const scopedCSS = getScopedCSS(content.cssCode);

  return (
    <section id={sectionId} className={containerClasses} style={sectionStyle}>
      {content.isVisible !== false && (
        <div className="container mx-auto px-4">
          {/* Section title and description (only if visible) */}
          {content.title && (
            <div className="mb-6">
              <h2 className="text-2xl font-bold mb-2">{content.title}</h2>
              {content.description && (
                <p className="text-gray-600">{content.description}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Custom Code Preview - rendered in isolated iframe to prevent errors from crashing the editor */}
      {(content.htmlCode || content.jsCode || scopedCSS) && (
        <div className={content.isVisible !== false ? "container mx-auto px-4" : ""}>
          <iframe
            title="Custom Code Preview"
            sandbox="allow-scripts"
            style={{
              width: '100%',
              minHeight: '200px',
              border: 'none',
              background: 'transparent'
            }}
            srcDoc={`
              <!DOCTYPE html>
              <html>
                <head>
                  <style>
                    body { margin: 0; padding: 0; font-family: ${designProps.fontFamily || 'inherit'}; }
                    ${scopedCSS || ''}
                    ${content.cssScope === 'inline' ? (content.cssCode || '') : ''}
                  </style>
                </head>
                <body>
                  ${content.htmlCode || ''}
                  ${content.jsCode ? `
                    <script>
                      (function() {
                        try {
                          eval(${JSON.stringify(content.jsCode)});
                        } catch (e) {
                          console.debug('[CustomCode Preview] Error:', e.message);
                        }
                      })();
                    </script>
                  ` : ''}
                </body>
              </html>
            `}
          />
        </div>
      )}
    </section>
  );
};

const BannerSection = ({ section, colorTheme, designProps, getMaxWidthClass }) => {
  const content = section.content || {};
  
  // If no text is configured, show empty state
  if (!content.text) {
    return (
      <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <div className="text-gray-500">
          <div className="text-4xl mb-4">🎌</div>
          <h3 className="text-lg font-medium mb-2">Banner Section</h3>
          <p className="text-sm">Configure the banner text to display content</p>
        </div>
      </div>
    );
  }

  // Height mapping
  const heightMap = {
    small: '200px',
    medium: '400px', 
    large: '600px',
    full: '100vh'
  };

  // Text size mapping for slider values 1-7
  const textSizeMap = {
    1: 'text-lg md:text-xl',
    2: 'text-xl md:text-2xl',
    3: 'text-2xl md:text-3xl',
    4: 'text-3xl md:text-4xl lg:text-5xl',
    5: 'text-4xl md:text-5xl lg:text-6xl',
    6: 'text-5xl md:text-6xl lg:text-7xl',
    7: 'text-6xl md:text-7xl lg:text-8xl xl:text-9xl'
  };

  const bannerHeight = heightMap[content.height] || heightMap.medium;
  const textSize = textSizeMap[content.textSize] || textSizeMap[4];
  const textColor = content.textColor || '#ffffff';
  const overlayColor = content.overlayColor || '#000000';
  const overlayOpacity = (content.overlayOpacity || 50) / 100;

  const bannerStyle = {
    height: bannerHeight,
    backgroundColor: content.backgroundImage ? 'transparent' : overlayColor,
    backgroundImage: content.backgroundImage && content.backgroundImageUrl 
      ? `linear-gradient(rgba(${parseInt(overlayColor.slice(1, 3), 16)}, ${parseInt(overlayColor.slice(3, 5), 16)}, ${parseInt(overlayColor.slice(5, 7), 16)}, ${overlayOpacity}), rgba(${parseInt(overlayColor.slice(1, 3), 16)}, ${parseInt(overlayColor.slice(3, 5), 16)}, ${parseInt(overlayColor.slice(5, 7), 16)}, ${overlayOpacity})), url(${resolveImageUrl(content.backgroundImageUrl)})`
      : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    fontFamily: designProps.fontFamily
  };

  const textStyle = {
    color: textColor,
    textShadow: content.textShadow ? '2px 2px 4px rgba(0, 0, 0, 0.5)' : 'none'
  };

  return (
    <section className={`relative w-full ${getMaxWidthClass()}`}>
      <div className="w-full flex items-center justify-center" style={bannerStyle}>
        <div className="container mx-auto px-4 text-center">
          <h1 
            className={`font-bold ${textSize}`}
            style={textStyle}
          >
            {content.text}
          </h1>
        </div>
      </div>
      {content.backgroundImageUrlCredit && (
        <div className="absolute bottom-2 right-2 text-xs text-white opacity-70 bg-black bg-opacity-50 px-2 py-1 rounded">
          Photo by{' '}
          <a 
            href={content.backgroundImageUrlCredit.photographerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-300 hover:underline"
          >
            {content.backgroundImageUrlCredit.photographer}
          </a>
          {' '}on Unsplash
        </div>
      )}
    </section>
  );
};

const ArticleSection = ({ section, colorTheme, businessInfo, designProps, getHeadingStyle, getImageStyle, getMaxWidthClass }) => {
  const content = section.content || {};
  
  // Get color styles based on selected color
  const colorMap = {
    slate: { bg: '#f8fafc', text: '#1e293b', border: '#e2e8f0' },
    gray: { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' },
    blue: { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    indigo: { bg: '#e0e7ff', text: '#3730a3', border: '#a5b4fc' },
    purple: { bg: '#e9d5ff', text: '#7c2d12', border: '#c084fc' },
    red: { bg: '#fecaca', text: '#dc2626', border: '#f87171' }
  };
  
  const selectedColor = colorMap[content.color] || colorMap.slate;
  
  // Text alignment class
  const alignmentClass = content.alignment === 'justify' ? 'text-justify' : 
                        content.alignment === 'center' ? 'text-center' : 
                        content.alignment === 'right' ? 'text-right' : 'text-left';

  // Image styling
  const getImageClasses = () => {
    let classes = '';
    
    if (content.frame === 'circle') {
      // Circle frame needs specific sizing and object-fit
      classes = 'w-full h-full object-cover rounded-full';
    } else {
      // Standard image styling for other frames
      classes = 'w-full h-auto object-';
      classes += content.style === 'fit' ? 'contain' : 'cover';
      
      if (content.frame === 'none') {
        // No additional styling for none frame
      } else if (content.frame === 'rounded') {
        classes += ' rounded-lg';
      } else if (content.frame === 'wide') {
        classes += ' rounded-2xl';
      } else if (content.frame === 'square') {
        classes += ' aspect-square rounded-none';
      }
    }
    
    return classes;
  };

  // Image container styling
  const getImageContainerClasses = () => {
    let classes = 'article-image-container ';
    
    // Special handling for circle frame
    if (content.frame === 'circle') {
      if (content.placement === 'center') {
        classes += 'mx-auto w-64 h-64 mb-6';
      } else if (content.placement === 'right') {
        classes += 'md:float-right md:ml-6 md:mb-4 w-48 h-48 mb-6';
      } else {
        classes += 'md:float-left md:mr-6 md:mb-4 w-48 h-48 mb-6';
      }
    } else {
      // Standard container sizing for other frames
      if (content.placement === 'center') {
        classes += 'mx-auto max-w-lg mb-6';
      } else if (content.placement === 'right') {
        classes += 'md:float-right md:ml-6 md:mb-4 md:max-w-md mb-6';
      } else {
        classes += 'md:float-left md:mr-6 md:mb-4 md:max-w-md mb-6';
      }
    }
    
    return classes;
  };

  return (
    <section 
      className="py-16"
      style={{ backgroundColor: selectedColor.bg }}
    >
      <div className={`mx-auto px-4 ${getMaxWidthClass()}`}>
        <div className="article-content max-w-4xl mx-auto">
          {content.heading && (
            <h2 
              className={`font-bold mb-8 ${alignmentClass}`}
              style={{ 
                ...getHeadingStyle(1),
                color: selectedColor.text 
              }}
            >
              {content.heading}
            </h2>
          )}
          
          <div className={`article-body ${alignmentClass}`} style={{ color: selectedColor.text }}>
            {content.showImage !== false && content.image && (
              <div className={getImageContainerClasses()}>
                <img 
                  src={content.image} 
                  alt={content.imageAlt || ''} 
                  className={getImageClasses()}
                  loading="lazy"
                />
              </div>
            )}
            
            <div 
              className="article-text prose prose-lg max-w-none prose-headings:text-current prose-p:text-current prose-strong:text-current prose-em:text-current prose-ul:text-current prose-ol:text-current prose-li:text-current"
              style={{ 
                color: selectedColor.text,
                '--tw-prose-body': selectedColor.text,
                '--tw-prose-headings': selectedColor.text,
                '--tw-prose-bold': selectedColor.text,
                '--tw-prose-links': selectedColor.text
              }}
              dangerouslySetInnerHTML={{ __html: content.paragraph || '' }}
            />
            <style jsx>{`
              .article-text h1, .article-text h2, .article-text h3, .article-text h4, .article-text h5, .article-text h6 {
                font-weight: bold;
                margin: 1em 0 0.5em 0;
                line-height: 1.2;
              }
              .article-text h1 { font-size: 2em; }
              .article-text h2 { font-size: 1.5em; }
              .article-text h3 { font-size: 1.17em; }
              .article-text h4 { font-size: 1em; }
              .article-text h5 { font-size: 0.83em; }
              .article-text h6 { font-size: 0.67em; }
              .article-text p {
                margin: 1em 0;
                line-height: 1.6;
              }
              .article-text strong, .article-text b {
                font-weight: bold;
              }
              .article-text em, .article-text i {
                font-style: italic;
              }
              .article-text ul, .article-text ol {
                margin: 1em 0;
                padding-left: 2em;
              }
              .article-text ul li {
                list-style-type: disc;
                margin: 0.5em 0;
              }
              .article-text ol li {
                list-style-type: decimal;
                margin: 0.5em 0;
              }
              .article-text blockquote {
                border-left: 4px solid #e5e7eb;
                padding-left: 1em;
                margin: 1em 0;
                font-style: italic;
                color: #6b7280;
              }
              .article-text a {
                color: #2563eb;
                text-decoration: underline;
              }
              .article-text a:hover {
                color: #1d4ed8;
              }
              .article-text code {
                background-color: #f3f4f6;
                padding: 2px 4px;
                border-radius: 3px;
                font-family: monospace;
                font-size: 0.9em;
              }
            `}</style>
          </div>
        </div>
      </div>
    </section>
  );
};

// Cookie Banner Preview Component
const CookieBannerPreview = ({ colorTheme, designProps }) => {
  const [isVisible, setIsVisible] = useState(true);

  const getButtonRadius = () => {
    const buttonCorners = designProps.buttonCorners || 'soft';
    const radiusMap = {
      'square': '0px',
      'soft': '6px',
      'round': '12px',
      'oval': '28px'
    };
    return radiusMap[buttonCorners] || '6px';
  };

  if (!isVisible) return null;

  return (
    <div 
      className="absolute bottom-2 left-2 right-2 z-50 p-3 shadow-lg rounded"
      style={{ 
        backgroundColor: colorTheme.background,
        border: `1px solid ${colorTheme.accent}`,
        fontFamily: designProps.fontFamily
      }}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex-1">
          <p 
            style={{ 
              color: colorTheme.text,
              fontSize: `${Math.max((designProps.textSize || 15) - 2, 12)}px`,
              opacity: 0.9,
              lineHeight: '1.4'
            }}
          >
            We use cookies to improve your experience. By continuing, you consent to our use of cookies.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button 
            onClick={() => setIsVisible(false)}
            className="px-4 py-1.5 font-medium text-sm transition-colors hover:opacity-90"
            style={{
              backgroundColor: colorTheme.primary,
              color: colorTheme.background,
              borderRadius: getButtonRadius()
            }}
          >
            Accept
          </button>
          <button 
            onClick={() => setIsVisible(false)}
            className="px-4 py-1.5 font-medium text-sm border transition-colors hover:opacity-80"
            style={{
              color: colorTheme.text,
              borderColor: colorTheme.accent,
              backgroundColor: 'transparent',
              borderRadius: getButtonRadius()
            }}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
};

export default LivePreview;